// SPDX-License-Identifier: BUSL-1.1
/**
 * Drizzle ORM schema — all build.ceo ops tables.
 *
 * NOTE on Better Auth tables: the build-ceo-auth worker (packages/cli-auth/) owns
 * the `user`, `session`, `account`, `verification`, and `jwks` tables. Their
 * schema lives in `packages/cli-auth/src/schema/auth.ts` (authoritative for
 * build-ceo-auth's own queries) and is created by migration
 * `packages/core/drizzle/0014_better_auth_tables.sql` — placed in core's
 * drizzle folder because core's `drizzle/` is the single migration source for
 * the shared Hyperdrive DB. core itself does NOT query those tables. drz-003
 * (migrations must update schema.ts) intentionally does not apply to BA
 * tables. (Migrations are hand-applied per ADR-009 — see
 * `docs-internal/adr/ADR-009-CANONICAL-MIGRATION-STRATEGY.md`.)
 *
 * NOTE on MT-RUN-34 (Phase 0, NocoDB #4721): this file is the source of truth
 * for the migration system. The MT-RUN-33 audit found the migration *files*
 * (replayed onto a fresh DB) did not fully reproduce this schema — the gaps
 * were authoring bugs in 0011/0016 and a never-authored `pr_review_jobs`
 * table. The corrective forward migrations
 * `0027_mt_run_34_org_constraints.sql`,
 * `0028_mt_run_34_moderation_queue_reshape.sql`, and
 * `0029_mt_run_34_schema_replay_reconcile.sql` move the *replay* toward this
 * file (not the other way) — so the table definitions below are unchanged.
 * Verify with `tsx drizzle/scripts/audit-schema-gap.mjs --replay-only`: the
 * in-scope `schema.ts vs REPLAY` diff must be clean.
 */
import { bigint, boolean, check, index, integer, jsonb, numeric, pgTable, primaryKey, real, smallint, text, timestamp, unique, uniqueIndex, uuid, } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { opsOrganizations } from './organizations.js';
// ---------------------------------------------------------------------------
// ops_audit_logs
// ---------------------------------------------------------------------------
export const opsAuditLogs = pgTable('ops_audit_logs', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    actorId: text('actor_id').notNull(),
    actorType: text('actor_type').notNull(),
    actorIp: text('actor_ip'),
    action: text('action').notNull(),
    resourceType: text('resource_type').notNull(),
    resourceId: text('resource_id').notNull(),
    payload: jsonb('payload').notNull().default({}),
    metadata: jsonb('metadata').default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_audit_logs_pkey' }),
    check('ops_audit_logs_actor_type_check', sql `${t.actorType} IN ('human', 'agent', 'system')`),
    index('ops_audit_logs_actor_id_created_at_idx').on(t.actorId, t.createdAt.desc()),
    index('ops_audit_logs_resource_created_at_idx').on(t.resourceType, t.resourceId, t.createdAt.desc()),
    index('ops_audit_logs_action_created_at_idx').on(t.action, t.createdAt.desc()),
    index('ops_audit_logs_created_at_idx').on(t.createdAt.desc()),
    index('ops_audit_logs_organization_idx').on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_config_versions
// ---------------------------------------------------------------------------
export const opsConfigVersions = pgTable('ops_config_versions', {
    id: uuid('id').notNull().defaultRandom(),
    surfaceKey: text('surface_key').notNull(),
    version: integer('version').notNull(),
    config: jsonb('config').notNull(),
    environment: text('environment').notNull(),
    status: text('status').notNull(),
    createdBy: text('created_by').notNull(),
    comment: text('comment'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    promotedAt: timestamp('promoted_at', { withTimezone: true }),
    rollbackOf: integer('rollback_of'),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_config_versions_pkey' }),
    uniqueIndex('ops_config_versions_surface_env_version_key').on(t.surfaceKey, t.environment, t.version),
    check('ops_config_versions_status_check', sql `${t.status} IN ('draft', 'staged', 'promoted', 'rolled_back')`),
    index('ops_config_versions_surface_env_status_idx').on(t.surfaceKey, t.environment, t.status),
    index('ops_config_versions_surface_env_version_idx').on(t.surfaceKey, t.environment, t.version.desc()),
]);
// ---------------------------------------------------------------------------
// ops_config_surfaces
// ---------------------------------------------------------------------------
export const opsConfigSurfaces = pgTable('ops_config_surfaces', {
    key: text('key').notNull(),
    name: text('name').notNull(),
    description: text('description'),
    schema: jsonb('schema'),
    environments: text('environments')
        .array()
        .notNull()
        .default(sql `'{production}'`),
    organizationId: uuid('organization_id'),
    // --- CONFIG-LIFE-01: typed-config lifecycle columns ---
    configType: text('config_type').notNull().default('config'),
    lifecycleStage: text('lifecycle_stage').notNull().default('draft'),
    provider: text('provider').default('internal'),
    hypothesis: jsonb('hypothesis'),
    expiryDate: timestamp('expiry_date', { withTimezone: true }),
    rolloutPercentage: integer('rollout_percentage').default(0),
    killReason: text('kill_reason'),
    killedAt: timestamp('killed_at', { withTimezone: true }),
    graduatedAt: timestamp('graduated_at', { withTimezone: true }),
    sunsetAt: timestamp('sunset_at', { withTimezone: true }),
    sunsetPostmortem: text('sunset_postmortem'),
    owner: text('owner'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.key], name: 'ops_config_surfaces_pkey' }),
    check('ops_config_surfaces_config_type_check', sql `${t.configType} IN ('feature_flag', 'kill_switch', 'experiment', 'deployment', 'sdui', 'prompt_template', 'config')`),
    check('ops_config_surfaces_lifecycle_stage_check', sql `${t.lifecycleStage} IN ('hypothesis', 'draft', 'stage', 'rollout', 'decision', 'graduated', 'sunset')`),
    check('ops_config_surfaces_rollout_percentage_check', sql `${t.rolloutPercentage} IS NULL OR (${t.rolloutPercentage} >= 0 AND ${t.rolloutPercentage} <= 100)`),
    index('ops_config_surfaces_config_type_idx').on(t.configType),
    index('ops_config_surfaces_lifecycle_stage_idx').on(t.lifecycleStage),
    index('ops_config_surfaces_provider_idx').on(t.provider),
    index('ops_config_surfaces_expiry_date_idx').on(t.expiryDate),
    index('ops_config_surfaces_organization_idx').on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_capabilities
// ---------------------------------------------------------------------------
export const opsCapabilities = pgTable('ops_capabilities', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    key: text('key').notNull(),
    name: text('name').notNull(),
    description: text('description'),
    service: text('service').notNull(),
    domain: text('domain'),
    capabilityType: text('capability_type').notNull(),
    managementMode: text('management_mode').notNull().default('observed'),
    editMode: text('edit_mode').notNull().default('read_only'),
    riskLevel: text('risk_level').notNull().default('medium'),
    lifecycleStage: text('lifecycle_stage').notNull().default('draft'),
    owner: text('owner'),
    environments: text('environments')
        .array()
        .notNull()
        .default(sql `'{production}'`),
    sourceOfTruth: text('source_of_truth').notNull().default('external'),
    manifestRef: text('manifest_ref'),
    schemaRef: text('schema_ref'),
    metadata: jsonb('metadata').notNull().default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_capabilities_pkey' }),
    uniqueIndex('ops_capabilities_org_key').on(t.organizationId, t.key),
    check('ops_capabilities_capability_type_check', sql `${t.capabilityType} IN ('feature_release', 'operational_control', 'experiment', 'sdui_surface', 'prompt_surface', 'integration', 'invariant')`),
    check('ops_capabilities_management_mode_check', sql `${t.managementMode} IN ('observed', 'proposal_only', 'managed')`),
    check('ops_capabilities_edit_mode_check', sql `${t.editMode} IN ('read_only', 'guarded_edit', 'editable')`),
    check('ops_capabilities_risk_level_check', sql `${t.riskLevel} IN ('low', 'medium', 'high', 'critical')`),
    check('ops_capabilities_lifecycle_stage_check', sql `${t.lifecycleStage} IN ('draft', 'active', 'deprecated', 'sunset')`),
    check('ops_capabilities_source_of_truth_check', sql `${t.sourceOfTruth} IN ('external', 'build_ceo', 'shared')`),
    index('ops_capabilities_organization_idx').on(t.organizationId),
    index('ops_capabilities_service_idx').on(t.service),
    index('ops_capabilities_capability_type_idx').on(t.capabilityType),
    index('ops_capabilities_management_mode_idx').on(t.managementMode),
    index('ops_capabilities_edit_mode_idx').on(t.editMode),
    index('ops_capabilities_risk_level_idx').on(t.riskLevel),
    index('ops_capabilities_lifecycle_stage_idx').on(t.lifecycleStage),
]);
// ---------------------------------------------------------------------------
// ops_surface_bindings
// ---------------------------------------------------------------------------
export const opsSurfaceBindings = pgTable('ops_surface_bindings', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    capabilityId: uuid('capability_id').notNull(),
    bindingKey: text('binding_key').notNull(),
    bindingType: text('binding_type').notNull(),
    provider: text('provider').notNull(),
    environment: text('environment').notNull(),
    targetRef: text('target_ref').notNull(),
    pathSelector: text('path_selector'),
    accessMode: text('access_mode').notNull().default('read_only'),
    ownershipMode: text('ownership_mode').notNull().default('observed'),
    adapter: text('adapter'),
    schemaRef: text('schema_ref'),
    lastObservedVersion: text('last_observed_version'),
    lastObservedHash: text('last_observed_hash'),
    healthStatus: text('health_status').notNull().default('unknown'),
    metadata: jsonb('metadata').notNull().default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_surface_bindings_pkey' }),
    uniqueIndex('ops_surface_bindings_org_binding_key').on(t.organizationId, t.bindingKey),
    check('ops_surface_bindings_binding_type_check', sql `${t.bindingType} IN ('feature_flag', 'config_surface', 'sdui_surface', 'runtime_reference', 'invariant_reference', 'rollout_rule')`),
    check('ops_surface_bindings_access_mode_check', sql `${t.accessMode} IN ('read_only', 'proposal_only', 'read_write')`),
    check('ops_surface_bindings_ownership_mode_check', sql `${t.ownershipMode} IN ('observed', 'managed', 'shared')`),
    check('ops_surface_bindings_health_status_check', sql `${t.healthStatus} IN ('healthy', 'degraded', 'unknown', 'error')`),
    index('ops_surface_bindings_capability_idx').on(t.capabilityId),
    index('ops_surface_bindings_org_provider_env_idx').on(t.organizationId, t.provider, t.environment),
    index('ops_surface_bindings_org_binding_type_idx').on(t.organizationId, t.bindingType),
    index('ops_surface_bindings_target_ref_idx').on(t.targetRef),
]);
// ---------------------------------------------------------------------------
// ops_import_sources
// ---------------------------------------------------------------------------
export const opsImportSources = pgTable('ops_import_sources', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    key: text('key').notNull(),
    sourceType: text('source_type').notNull(),
    service: text('service').notNull(),
    environment: text('environment').notNull(),
    config: jsonb('config').notNull().default({}),
    ownershipMode: text('ownership_mode').notNull().default('observed'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_import_sources_pkey' }),
    uniqueIndex('ops_import_sources_org_key').on(t.organizationId, t.key),
    check('ops_import_sources_source_type_check', sql `${t.sourceType} IN ('repo_manifest', 'posthog', 'internal_api', 'manual_upload')`),
    check('ops_import_sources_ownership_mode_check', sql `${t.ownershipMode} IN ('observed', 'adoptable', 'managed')`),
    index('ops_import_sources_organization_idx').on(t.organizationId),
    index('ops_import_sources_service_env_idx').on(t.service, t.environment),
    index('ops_import_sources_source_type_idx').on(t.sourceType),
]);
// ---------------------------------------------------------------------------
// ops_import_runs
// ---------------------------------------------------------------------------
export const opsImportRuns = pgTable('ops_import_runs', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    importSourceId: uuid('import_source_id').notNull(),
    status: text('status').notNull().default('running'),
    sourceRevision: text('source_revision'),
    summary: jsonb('summary').notNull().default({}),
    rawSnapshot: jsonb('raw_snapshot').notNull().default({}),
    driftSummary: jsonb('drift_summary').notNull().default({}),
    generatedChangeSetId: uuid('generated_change_set_id'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull().defaultNow(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_import_runs_pkey' }),
    check('ops_import_runs_status_check', sql `${t.status} IN ('running', 'completed', 'failed', 'partial')`),
    index('ops_import_runs_organization_idx').on(t.organizationId),
    index('ops_import_runs_import_source_idx').on(t.importSourceId),
    index('ops_import_runs_status_idx').on(t.status),
    index('ops_import_runs_started_at_idx').on(t.startedAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_import_observations
// ---------------------------------------------------------------------------
export const opsImportObservations = pgTable('ops_import_observations', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    importRunId: uuid('import_run_id').notNull(),
    objectType: text('object_type').notNull(),
    externalKey: text('external_key').notNull(),
    normalizedKey: text('normalized_key'),
    payload: jsonb('payload').notNull().default({}),
    status: text('status').notNull().default('observed'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_import_observations_pkey' }),
    check('ops_import_observations_object_type_check', sql `${t.objectType} IN ('capability_candidate', 'binding_candidate', 'unmapped_surface', 'unsupported')`),
    check('ops_import_observations_status_check', sql `${t.status} IN ('observed', 'mapped', 'ignored', 'error')`),
    index('ops_import_observations_organization_idx').on(t.organizationId),
    index('ops_import_observations_import_run_idx').on(t.importRunId),
    index('ops_import_observations_object_type_idx').on(t.objectType),
    index('ops_import_observations_external_key_idx').on(t.externalKey),
]);
// ---------------------------------------------------------------------------
// ops_change_sets
// ---------------------------------------------------------------------------
export const opsChangeSets = pgTable('ops_change_sets', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    key: text('key').notNull(),
    title: text('title').notNull(),
    description: text('description'),
    service: text('service'),
    environment: text('environment').notNull(),
    status: text('status').notNull().default('draft'),
    riskLevel: text('risk_level').notNull().default('medium'),
    authorId: text('author_id').notNull(),
    authorType: text('author_type').notNull(),
    requestedBy: text('requested_by'),
    approvedBy: text('approved_by'),
    relatedPrUrl: text('related_pr_url'),
    relatedCommitSha: text('related_commit_sha'),
    /**
     * CEO-Sprint-71 E2-S1-T2: importRunId links this change set to the
     * ceo-dna import run that informed it.  Cross-surface lineage relies on
     * this column for the session→change-set traversal.  Nullable for
     * change sets not derived from an import.
     */
    importRunId: text('import_run_id'),
    rolloutStrategy: jsonb('rollout_strategy').notNull().default({}),
    verificationPlan: jsonb('verification_plan').notNull().default({}),
    rollbackPlan: jsonb('rollback_plan').notNull().default({}),
    idempotencyKey: text('idempotency_key'),
    metadata: jsonb('metadata').notNull().default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    approvedAt: timestamp('approved_at', { withTimezone: true }),
    appliedAt: timestamp('applied_at', { withTimezone: true }),
    verifiedAt: timestamp('verified_at', { withTimezone: true }),
    rolledBackAt: timestamp('rolled_back_at', { withTimezone: true }),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_change_sets_pkey' }),
    uniqueIndex('ops_change_sets_org_key').on(t.organizationId, t.key),
    uniqueIndex('ops_change_sets_org_idempotency_key').on(t.organizationId, t.idempotencyKey),
    check('ops_change_sets_status_check', sql `${t.status} IN ('draft', 'validated', 'awaiting_approval', 'approved', 'executing', 'partially_applied', 'applied', 'verified', 'rolled_back', 'failed', 'rejected', 'cancelled', 'expired')`),
    check('ops_change_sets_risk_level_check', sql `${t.riskLevel} IN ('low', 'medium', 'high', 'critical')`),
    check('ops_change_sets_author_type_check', sql `${t.authorType} IN ('human', 'agent', 'system')`),
    index('ops_change_sets_organization_idx').on(t.organizationId),
    index('ops_change_sets_service_idx').on(t.service),
    index('ops_change_sets_environment_idx').on(t.environment),
    index('ops_change_sets_status_idx').on(t.status),
    index('ops_change_sets_author_id_idx').on(t.authorId),
    index('ops_change_sets_risk_level_idx').on(t.riskLevel),
    /**
     * CEO-Sprint-71 E2-S1-T2: index for cross-surface lineage query —
     * given an importRunId, find all change sets informed by that run.
     */
    index('ops_change_sets_import_run_idx').on(t.importRunId),
]);
// ---------------------------------------------------------------------------
// ops_change_set_items
// ---------------------------------------------------------------------------
export const opsChangeSetItems = pgTable('ops_change_set_items', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    changeSetId: uuid('change_set_id').notNull(),
    capabilityId: uuid('capability_id'),
    surfaceBindingId: uuid('surface_binding_id'),
    itemType: text('item_type').notNull(),
    action: text('action').notNull(),
    currentSnapshot: jsonb('current_snapshot').notNull().default({}),
    proposedSnapshot: jsonb('proposed_snapshot').notNull().default({}),
    diffSummary: jsonb('diff_summary').notNull().default({}),
    executionStatus: text('execution_status').notNull().default('pending'),
    executionOrder: integer('execution_order').notNull().default(0),
    adapter: text('adapter'),
    metadata: jsonb('metadata').notNull().default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_change_set_items_pkey' }),
    check('ops_change_set_items_item_type_check', sql `${t.itemType} IN ('capability_metadata', 'binding_value', 'binding_rollout', 'binding_state', 'code_reference')`),
    check('ops_change_set_items_action_check', sql `${t.action} IN ('create', 'update', 'delete', 'rebind', 'adopt', 'release')`),
    check('ops_change_set_items_execution_status_check', sql `${t.executionStatus} IN ('pending', 'validated', 'applied', 'failed', 'rolled_back', 'skipped')`),
    index('ops_change_set_items_organization_idx').on(t.organizationId),
    index('ops_change_set_items_change_set_idx').on(t.changeSetId),
    index('ops_change_set_items_capability_idx').on(t.capabilityId),
    index('ops_change_set_items_surface_binding_idx').on(t.surfaceBindingId),
    index('ops_change_set_items_execution_status_idx').on(t.executionStatus),
]);
// ---------------------------------------------------------------------------
// ops_change_set_events
// ---------------------------------------------------------------------------
export const opsChangeSetEvents = pgTable('ops_change_set_events', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    changeSetId: uuid('change_set_id').notNull(),
    itemId: uuid('item_id'),
    eventType: text('event_type').notNull(),
    actorId: text('actor_id').notNull(),
    actorType: text('actor_type').notNull(),
    payload: jsonb('payload').notNull().default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_change_set_events_pkey' }),
    check('ops_change_set_events_event_type_check', sql `${t.eventType} IN ('created', 'validated', 'approval_requested', 'approved', 'execution_started', 'item_applied', 'item_failed', 'applied', 'verified', 'failed', 'rollback_started', 'rolled_back', 'rejected')`),
    check('ops_change_set_events_actor_type_check', sql `${t.actorType} IN ('human', 'agent', 'system')`),
    index('ops_change_set_events_organization_idx').on(t.organizationId),
    index('ops_change_set_events_change_set_idx').on(t.changeSetId),
    index('ops_change_set_events_item_idx').on(t.itemId),
    index('ops_change_set_events_event_type_idx').on(t.eventType),
    index('ops_change_set_events_created_at_idx').on(t.createdAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_emergency_actions
// ---------------------------------------------------------------------------
export const opsEmergencyActions = pgTable('ops_emergency_actions', {
    id: uuid('id').notNull().defaultRandom(),
    actionType: text('action_type').notNull(),
    target: text('target').notNull(),
    previousState: jsonb('previous_state'),
    newState: jsonb('new_state').notNull(),
    reason: text('reason'),
    actorId: text('actor_id').notNull(),
    autoRevertAt: timestamp('auto_revert_at', { withTimezone: true }),
    revertedAt: timestamp('reverted_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_emergency_actions_pkey' }),
    check('ops_emergency_actions_action_type_check', sql `${t.actionType} IN ('kill_switch', 'cache_purge', 'flag_override')`),
    index('ops_emergency_actions_type_created_at_idx').on(t.actionType, t.createdAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_api_keys
// ---------------------------------------------------------------------------
export const opsApiKeys = pgTable('ops_api_keys', {
    id: uuid('id').notNull().defaultRandom(),
    keyHash: text('key_hash').notNull().unique('ops_api_keys_key_hash_key'),
    name: text('name').notNull(),
    role: text('role').notNull().default('admin'),
    organizationId: uuid('organization_id'),
    actorId: text('actor_id').notNull(),
    actorType: text('actor_type').notNull().default('human'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_api_keys_pkey' }),
    check('ops_api_keys_role_check', sql `${t.role} IN ('super_admin', 'admin', 'viewer', 'reader')`),
    check('ops_api_keys_actor_type_check', sql `${t.actorType} IN ('human', 'agent', 'system')`),
    index('ops_api_keys_hash_active_idx')
        .on(t.keyHash)
        .where(sql `${t.revokedAt} IS NULL`),
    index('ops_api_keys_organization_idx').on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_registration_tokens — Short-lived bootstrap tokens (MT-RUN-07, NocoDB #4582)
// ---------------------------------------------------------------------------
//
// Issued by org admins to onboard new runners. The runner exchanges a
// registration token for a long-lived agent API key via
// `POST /api/runners/register` on first boot. Multi-use within TTL
// (1hr default, 24h max) so an operator can scale a fleet/ASG with one
// mint. Revoke explicitly to kill an outstanding token.
//
// Same hashing model as `ops_api_keys.key_hash` — the plaintext token is
// shown to the operator ONCE at mint time, never persisted. Verification
// in `/api/runners/register` recomputes SHA-256 over the presented token
// and looks up by `token_hash`.
export const opsRegistrationTokens = pgTable('ops_registration_tokens', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    tokenHash: text('token_hash').notNull().unique('ops_registration_tokens_token_hash_key'),
    scopes: jsonb('scopes').notNull().default(['runner:register']),
    label: text('label'),
    createdBy: text('created_by').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    lastUsedAt: timestamp('last_used_at', { withTimezone: true }),
    useCount: integer('use_count').notNull().default(0),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_registration_tokens_pkey' }),
    check('ops_registration_tokens_expires_check', sql `${t.expiresAt} > ${t.createdAt}`),
    check('ops_registration_tokens_use_count_check', sql `${t.useCount} >= 0`),
    // Active-token lookup: org admins listing live tokens / revoke flow.
    // Partial index excludes revoked rows so the index stays tight as
    // history accumulates.
    index('ops_registration_tokens_org_active_idx')
        .on(t.organizationId, t.expiresAt.desc())
        .where(sql `${t.revokedAt} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_admin_users
// ---------------------------------------------------------------------------
export const opsAdminUsers = pgTable('ops_admin_users', {
    id: uuid('id').notNull().defaultRandom(),
    authUserId: text('auth_user_id').notNull().unique('ops_admin_users_auth_user_id_key'),
    email: text('email').notNull(),
    displayName: text('display_name'),
    role: text('role').notNull().default('viewer'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_admin_users_pkey' }),
    check('ops_admin_users_role_check', sql `${t.role} IN ('super_admin', 'admin', 'viewer')`),
    index('ops_admin_users_active_auth_user_id_idx')
        .on(t.authUserId)
        .where(sql `${t.isActive} = true`),
]);
// ---------------------------------------------------------------------------
// ops_agent_sessions — FOUNDATION-01 (ADR-004)
// ---------------------------------------------------------------------------
//
// Persistent record of every governed Anthropic Managed Agents session.
// Written by GovernedSessionManager on create + terminate (single chokepoint,
// per ADR-004 Decision 1). HTTP routes that bypass governance do NOT write
// here — that's intentional, to keep audit attribution clean.
//
// ma_status  = verbatim MA transport state (idle/running/rescheduling/terminated).
// outcome    = governance-derived settlement reason. Nullable while session is
//              still active. MA does not distinguish success from failure in the
//              transport layer — the distinction lives here.
//
// Cost is snapshotted at persist time using the canonical calculateSessionCost
// from @build-ceo/shared-constants. Rollups (DASH-19) can recompute from the raw
// token columns if model rates change retroactively.
//
// metadata jsonb stores project namespacing (DOGFOOD-01 Pattern 2 —
// `metadata->>'project' = 'dd'`) and is extensible for fleet_id, team_id,
// policy_version without migrations. GIN-indexed for containment queries.
export const opsAgentSessions = pgTable('ops_agent_sessions', {
    // Identity
    id: text('id').notNull(), // Anthropic MA session ID (stable)
    organizationId: uuid('organization_id'),
    agentId: text('agent_id'), // nullable for ad-hoc sessions
    actorId: text('actor_id').notNull(), // join key → ops_api_keys.actor_id (no FK — ops_api_keys.actor_id is not UNIQUE yet)
    // Model + status
    model: text('model').notNull(),
    maStatus: text('ma_status').notNull(),
    outcome: text('outcome'), // null while active
    // Timing
    // GitHub repo attribution (Sprint 42 S3)
    repoId: integer('repo_id'),
    repoName: text('repo_name'),
    repoFullName: text('repo_full_name'),
    orgId: integer('org_id'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    activeSeconds: integer('active_seconds').notNull().default(0),
    durationSeconds: integer('duration_seconds').notNull().default(0),
    // Usage snapshot
    inputTokens: integer('input_tokens').notNull().default(0),
    outputTokens: integer('output_tokens').notNull().default(0),
    cacheReadTokens: integer('cache_read_tokens').notNull().default(0),
    cacheCreation5mTokens: integer('cache_creation_5m_tokens').notNull().default(0),
    cacheCreation1hTokens: integer('cache_creation_1h_tokens').notNull().default(0),
    webSearchRequests: integer('web_search_requests').notNull().default(0),
    // Cost (USD, snapshot at persist time)
    totalCostUsd: numeric('total_cost_usd', { precision: 10, scale: 6 }).notNull().default('0'),
    // Governance context
    budgetScope: text('budget_scope'),
    budgetLimitUsd: numeric('budget_limit_usd', { precision: 10, scale: 6 }),
    // Extensibility — DOGFOOD-01 project namespacing + future dimensions
    metadata: jsonb('metadata').notNull().default({}),
    // Timestamps
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_agent_sessions_pkey' }),
    check('ops_agent_sessions_ma_status_check', sql `${t.maStatus} IN ('idle', 'running', 'rescheduling', 'terminated')`),
    check('ops_agent_sessions_outcome_check', sql `${t.outcome} IS NULL OR ${t.outcome} IN ('success', 'budget_exceeded', 'manual_terminate', 'policy_denied', 'error')`),
    check('ops_agent_sessions_outcome_consistency_check', sql `${t.outcome} IS NULL OR ${t.maStatus} = 'terminated'`),
    // Dashboard time-series (DASH-19 rollup hot path) — partial skips active sessions
    index('ops_agent_sessions_completed_at_idx')
        .on(t.completedAt.desc())
        .where(sql `${t.completedAt} IS NOT NULL`),
    // "My sessions" / per-actor billing
    index('ops_agent_sessions_actor_completed_at_idx').on(t.actorId, t.completedAt.desc()),
    // Model-level cost aggregation (DASH-19/21)
    index('ops_agent_sessions_model_completed_at_idx').on(t.model, t.completedAt.desc()),
    // Active sessions lookup (FOUNDATION-02 reconciliation sweeper) — partial
    index('ops_agent_sessions_ma_status_idx')
        .on(t.maStatus)
        .where(sql `${t.maStatus} != 'terminated'`),
    // Project filtering per DOGFOOD-01 (`metadata @> '{"project":"dd"}'`)
    index('ops_agent_sessions_metadata_gin_idx').using('gin', t.metadata),
    index('ops_agent_sessions_organization_idx').on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_session_favorites — CEO-Sprint-76 E1-S1-T1
//
// Per-operator bookmarks for archived sessions. Each operator can mark a session
// as favorite once (unique constraint on organization + operator + session).
// Not shared across operators — scoped to the individual operator's identity.
// ---------------------------------------------------------------------------
export const opsSessionFavorites = pgTable('ops_session_favorites', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id').notNull(),
    operatorId: text('operator_id').notNull(),
    sessionId: text('session_id').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_session_favorites_pkey' }),
    // One favorite per operator per session (idempotent)
    unique('ops_session_favorites_unique_operator_session').on(t.organizationId, t.operatorId, t.sessionId),
    index('ops_session_favorites_operator_idx').on(t.operatorId),
    index('ops_session_favorites_organization_idx').on(t.organizationId),
    index('ops_session_favorites_session_idx').on(t.sessionId),
]);
// ---------------------------------------------------------------------------
// ops_drift — CEO-Sprint-76 E2-S1-T1
//
// Records when execution differs from approval (drift detection).
// Two models:
//   1. State drift — actual production differs from desired state (GitOps)
//   2. Lineage drift — production running something whose origin differs from approved
//
// Drift types:
//   POLICY_VERSION_MISMATCH   — execution version differs from session snapshot
//   CAPABILITY_VIOLATION       — action outside granted capability set
//   POLICY_CHANGED_MID_SESSION — active policy changed without defined behavior
//   MISSING_GOVERNANCE_EVENT  — required event absent or unverifiable
//   UNATTESTED_EXECUTION       — outcome not tied to authorized execution
//
// Status lifecycle: open → acknowledged → resolved / dismissed
// ---------------------------------------------------------------------------
export const opsDrift = pgTable('ops_drift', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id').notNull(),
    // Expected (approved) state
    policyId: text('policy_id').notNull(),
    policyVersion: text('policy_version').notNull(),
    policyHash: text('policy_hash'), // sha256 of policy at approval time
    // Observed state
    eventId: text('event_id'), // the event that triggered detection
    observedPolicyVersion: text('observed_policy_version'),
    observedPolicyHash: text('observed_policy_hash'),
    // What drifted
    capability: text('capability'), // the capability that drifted
    expectedValue: text('expected_value'),
    observedValue: text('observed_value'),
    // Classification
    driftType: text('drift_type').notNull(),
    // 'POLICY_VERSION_MISMATCH' | 'CAPABILITY_VIOLATION' |
    // 'POLICY_CHANGED_MID_SESSION' | 'MISSING_GOVERNANCE_EVENT' |
    // 'UNATTESTED_EXECUTION'
    status: text('status').notNull().default('open'),
    // 'open' | 'acknowledged' | 'resolved' | 'dismissed'
    // Deduplication key — unique per (org, policy_id, drift_type, event_id)
    dedupeKey: text('dedupe_key').notNull(),
    // Detector provenance
    detectorVersion: text('detector_version').notNull(),
    // Attribution
    changeSetId: text('change_set_id'),
    sessionId: text('session_id'),
    actorId: text('actor_id'),
    // Timestamps
    detectedAt: timestamp('detected_at', { withTimezone: true }).notNull().defaultNow(),
    acknowledgedAt: timestamp('acknowledged_at', { withTimezone: true }),
    resolvedAt: timestamp('resolved_at', { withTimezone: true }),
    resolutionNote: text('resolution_note'),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_drift_pkey' }),
    // Note: partial unique index enforced via migration 0075
    //   CREATE UNIQUE INDEX ops_drift_dedupe_idx
    //     ON ops_drift (organization_id, dedupe_key) WHERE status = 'open';
    // Drizzle schema does not support partial unique indexes — DB enforces this.
    unique('ops_drift_dedupe_idx').on(t.organizationId, t.dedupeKey),
    index('ops_drift_org_status_idx').on(t.organizationId, t.status, t.detectedAt.desc()),
    index('ops_drift_policy_idx').on(t.organizationId, t.policyId, t.detectedAt.desc()),
    index('ops_drift_session_idx').on(t.sessionId),
    index('ops_drift_changeset_idx').on(t.changeSetId),
]);
// ---------------------------------------------------------------------------
// ops_episodes — CEO-Sprint-1 T4
//
// Telemetry episode records derived from ceo-dna archived sessions.
// Written by the archivist pipeline (CEO-S1-T2) on session push.
// DOGFOOD-01: project = 'dogfood-01' namespace.
// ADR-010 G6: recording_url / decisions_json / artifacts_refs stored here;
// the GET /api/agents/:agentId/episodes/:episodeId endpoint (Archive G1)
// projects ops_episodes + opsAgentSessions into EpisodeView.
//
// Idempotent upserts via archivist pipeline; no separate CRUD route needed
// in v0.1 (CLI writes via archivist, reads via archive-episodes projection).
// ---------------------------------------------------------------------------
export const opsEpisodes = pgTable('ops_episodes', {
    id: text('id').notNull(),
    organizationId: uuid('organization_id'),
    agentId: text('agent_id').notNull(),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    endedAt: timestamp('ended_at', { withTimezone: true }),
    title: text('title'),
    narrative: text('narrative'),
    recordingUrl: text('recording_url'),
    decisionsJson: jsonb('decisions_json').notNull().default([]),
    artifactsRefs: jsonb('artifacts_refs').notNull().default([]),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id] }),
    index('ops_episodes_agent_id_idx').on(t.agentId),
    index('ops_episodes_started_at_idx').on(t.startedAt.desc()),
    index('ops_episodes_organization_idx')
        .on(t.organizationId)
        .where(sql `${t.organizationId} IS NOT NULL`),
]);
// ---------------------------------------------------------------------------
// ops_episode_audit_trail — CEO-S2-T3
//
// Evidence chain per episode — records every significant decision, action, and
// governance check that occurred during a ceo-dna session, creating a queryable
// forensic record of "what happened and why" per episode.
//
// Written by the archivist pipeline (CEO-S1-T2) at session ingest time; read by
// the Archive projection (ADR-010 G6) to build the evidence chain in EpisodeView.
//
// ADR-008 / adp-003: append-only, immutable once recorded.
// ---------------------------------------------------------------------------
export const opsEpisodeAuditTrail = pgTable('ops_episode_audit_trail', {
    /** Unique identifier for this evidence record. */
    id: uuid('id').notNull().defaultRandom(),
    /** Episode this evidence belongs to (join key → ops_episodes.id). */
    episodeId: text('episode_id').notNull(),
    /**
     * Kind of evidence record.
     *
     * - `tool_use`   — a tool was invoked; payload = tool name + args summary
     * - `commit`     — a git commit was made; payload = commit ref + message
     * - `moderation` — a governance/moderation event fired; payload = rule + action
     * - `synthesis`  — LLM synthesis completed; payload = model + tokens + cost
     * - `flag`       — a flag/scoped behavior change; payload = flag key + value
     */
    kind: text('kind').notNull(),
    /** Short human-readable label for this evidence item. */
    label: text('label').notNull(),
    /**
     * JSON payload with the full structured evidence.
     *
     * Shape varies by `kind`:
     *   tool_use   — { toolName, argsSummary, costUsd, model, durationMs }
     *   commit     — { sha, message, branch, filesChanged, insertions, deletions }
     *   moderation — { rule, action, severity, flaggedContent }
     *   synthesis  — { model, inputTokens, outputTokens, costUsd, durationMs }
     *   flag       — { flagKey, flagValue, priorValue }
     */
    payload: jsonb('payload').notNull().default({}),
    /**
     * Optional pointer to the source NormalizedEvent in ceo-dna NDJSON.
     * Format: `<session-id>:<event-occurred-at-iso>` (opaque join key).
     */
    sourceEventRef: text('source_event_ref'),
    /** ISO-8601 of when the event occurred (from ceo-dna event). */
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    /** Actor (agent/user) ID from the ceo-dna session. */
    actorId: text('actor_id'),
    /** Surface key (tool identifier) from the ceo-dna event. */
    surfaceKey: text('surface_key'),
    /** ISO-8601 of when this record was written. */
    recordedAt: timestamp('recorded_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    index('ops_episode_audit_trail_episode_id_idx').on(t.episodeId),
    index('ops_episode_audit_trail_occurred_at_idx').on(t.occurredAt.desc()),
    index('ops_episode_audit_trail_kind_idx').on(t.kind),
]);
// ---------------------------------------------------------------------------
// ops_moderation_queue — CEO-MOD-01
// ---------------------------------------------------------------------------
//
// Persistent record of every governed tool call that hit a `manual` policy
// gate and is awaiting (or has received) human approval. Written by
// `DrizzleModerationStore.enqueue` (called from GovernedSessionManager when a
// tool confirmation routes through the `manual` gate) and updated by
// `DrizzleModerationStore.resolve` from `POST /api/moderation/:id/resolve`.
//
// IMPORTANT: resolving a row here only persists the moderator decision. The
// agent-side tool confirmation is delivered separately by GovernedSessionManager
// when it next polls the queue — keeping HTTP routes free of transport coupling.
//
// `tool_input` is denormalised JSONB for forensic display in the moderation
// inbox UI; `policy_gate` records which gate fired so the dashboard can show
// gate-level distributions without joining adapter metadata.
export const opsModerationQueue = pgTable('ops_moderation_queue', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    sessionId: text('session_id').notNull(),
    toolUseId: text('tool_use_id').notNull(),
    toolName: text('tool_name').notNull(),
    toolInput: jsonb('tool_input').notNull(),
    policyGate: text('policy_gate').notNull(),
    status: text('status').notNull().default('pending'),
    sessionThreadId: text('session_thread_id'),
    denyMessage: text('deny_message'),
    resolvedBy: text('resolved_by'),
    resolvedAt: timestamp('resolved_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_moderation_queue_pkey' }),
    check('ops_moderation_queue_policy_gate_check', sql `${t.policyGate} IN ('safe', 'guarded', 'manual')`),
    check('ops_moderation_queue_status_check', sql `${t.status} IN ('pending', 'approved', 'rejected', 'escalated', 'cancelled', 'expired')`),
    // Inbox / badge: pending items newest-first
    index('ops_moderation_queue_status_created_at_idx').on(t.status, t.createdAt.desc()),
    // Per-session lookup (governance manager polls here on resume)
    index('ops_moderation_queue_session_id_idx').on(t.sessionId),
    index('ops_moderation_queue_organization_idx').on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_governance_approval_requests — canonical approval-request ledger
// ---------------------------------------------------------------------------
//
// First-class durable approval requests for governed local sessions.
// Session metadata keeps checkpoint/resume payload support, but approval truth
// lives here so routes can query status without reconstructing semantics from
// JSONB snapshots plus moderation rows.
export const opsGovernanceApprovalRequests = pgTable('ops_governance_approval_requests', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id'),
    sessionId: text('session_id').notNull(),
    sessionFamily: text('session_family').notNull().default('governance_session'),
    toolUseId: text('tool_use_id').notNull(),
    toolName: text('tool_name').notNull(),
    toolInput: jsonb('tool_input').notNull(),
    policyGate: text('policy_gate').notNull(),
    policyVersion: text('policy_version'),
    riskClassification: text('risk_classification'),
    status: text('status').notNull().default('pending'),
    requestedBy: text('requested_by').notNull(),
    sessionThreadId: text('session_thread_id'),
    checkpointSeq: integer('checkpoint_seq'),
    stateHash: text('state_hash'),
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    denyMessage: text('deny_message'),
    resolvedBy: text('resolved_by'),
    resolvedAt: timestamp('resolved_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_governance_approval_requests_pkey' }),
    check('ops_governance_approval_requests_policy_gate_check', sql `${t.policyGate} IN ('safe', 'guarded', 'manual')`),
    check('ops_governance_approval_requests_status_check', sql `${t.status} IN ('pending', 'approved', 'rejected', 'expired', 'cancelled', 'escalated')`),
    check('ops_governance_approval_requests_risk_classification_check', sql `${t.riskClassification} IS NULL OR ${t.riskClassification} IN ('low', 'medium', 'high', 'critical')`),
    uniqueIndex('ops_governance_approval_requests_session_tool_use_idx').on(t.sessionId, t.toolUseId),
    index('ops_governance_approval_requests_status_created_at_idx').on(t.status, t.createdAt.desc()),
    index('ops_governance_approval_requests_session_id_idx').on(t.sessionId),
    index('ops_governance_approval_requests_organization_idx').on(t.organizationId),
]);
// Re-export organizations and memberships from separate file
// (split to comply with line count limits)
export { opsOrganizations, } from './organizations.js';
export { opsOrgMemberships, } from './organizations.js';
// ---------------------------------------------------------------------------
// ops_org_invitations — Org membership invitations (CEO-V3, Task #4360)
// ---------------------------------------------------------------------------
export const opsOrgInvitations = pgTable('ops_org_invitations', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    email: text('email').notNull(),
    role: text('role').notNull().default('viewer'),
    invitedBy: text('invited_by').notNull(),
    token: text('token').notNull().unique(),
    status: text('status').notNull().default('pending'),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    check('ops_org_invitations_status_check', sql `${t.status} IN ('pending', 'accepted', 'expired', 'revoked')`),
    index('ops_org_invitations_organization_id_idx').on(t.organizationId),
    index('ops_org_invitations_token_idx').on(t.token),
    index('ops_org_invitations_email_idx').on(t.email),
]);
// ---------------------------------------------------------------------------
// ops_budget_usage — Budget tracking per (organization_id, scope) (Task #4271)
//
// MT-RUN-23 (NocoDB #4648): organization_id added so the same `scope` key
// (operator id, factory id, fleet slug) can carry separate budget state per
// tenant. Identity is enforced via the compound UNIQUE NULLS NOT DISTINCT
// constraint declared below (replaces the prior single-column PK on `scope`).
// Migration 0026 ships the SQL. Column is nullable to admit legacy rows
// during the backfill window; new writes set it from admin context.
// ---------------------------------------------------------------------------
export const opsBudgetUsage = pgTable('ops_budget_usage', {
    organizationId: uuid('organization_id').references(() => opsOrganizations.id, {
        onDelete: 'cascade',
    }),
    scope: text('scope').notNull(),
    totalBudgetUsd: numeric('total_budget_usd', { precision: 10, scale: 2 }),
    spentUsd: numeric('spent_usd', { precision: 10, scale: 6 }).notNull().default('0'),
    reservedUsd: numeric('reserved_usd', { precision: 10, scale: 6 }).notNull().default('0'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    // MT-RUN-23: identity is (organization_id, scope), NULLS NOT DISTINCT so
    // the legacy NULL-org backfill lane stays enforced. Matches the SQL
    // shipped by migration 0026.
    unique('ops_budget_usage_org_scope_key').on(t.organizationId, t.scope).nullsNotDistinct(),
    index('ops_budget_usage_total_budget_idx').on(t.totalBudgetUsd.desc()),
    index('ops_budget_usage_organization_id_idx').on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_pi_dev_sessions — pi.dev agent session tracking
// ---------------------------------------------------------------------------
export const opsPiDevSessions = pgTable('ops_pi_dev_sessions', {
    id: uuid('id').notNull().defaultRandom(),
    sessionId: text('session_id').notNull().unique(),
    agentId: text('agent_id').notNull(),
    status: text('status').notNull().default('active'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    endedAt: timestamp('ended_at', { withTimezone: true }),
    durationSeconds: integer('duration_seconds'),
    inputTokens: integer('input_tokens').notNull().default(0),
    outputTokens: integer('output_tokens').notNull().default(0),
    cost: numeric('cost', { precision: 10, scale: 6 }).notNull().default('0'),
    toolCallCount: integer('tool_call_count').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_pi_dev_sessions_pkey' }),
    check('ops_pi_dev_sessions_status_check', sql `${t.status} IN ('active', 'completed', 'terminated')`),
    index('ops_pi_dev_sessions_agent_id_idx').on(t.agentId),
    index('ops_pi_dev_sessions_status_idx').on(t.status),
    index('ops_pi_dev_sessions_started_at_idx').on(t.startedAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_pi_dev_tool_calls — pi.dev tool call audit
// ---------------------------------------------------------------------------
export const opsPiDevToolCalls = pgTable('ops_pi_dev_tool_calls', {
    id: uuid('id').notNull().defaultRandom(),
    sessionId: text('session_id').notNull(),
    tool: text('tool').notNull(),
    toolName: text('tool_name').notNull(),
    arguments: jsonb('arguments'),
    result: jsonb('result'),
    status: text('status').notNull(),
    policyGate: text('policy_gate').notNull().default('safe'),
    approvedBy: text('approved_by'),
    blockedBy: text('blocked_by'),
    durationMs: integer('duration_ms').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_pi_dev_tool_calls_pkey' }),
    check('ops_pi_dev_tool_calls_tool_check', sql `${t.tool} IN ('read', 'write', 'edit', 'bash', 'mcp', 'custom')`),
    check('ops_pi_dev_tool_calls_status_check', sql `${t.status} IN ('success', 'error', 'blocked')`),
    check('ops_pi_dev_tool_calls_policy_gate_check', sql `${t.policyGate} IN ('safe', 'guarded', 'manual')`),
    index('ops_pi_dev_tool_calls_session_id_idx').on(t.sessionId),
    index('ops_pi_dev_tool_calls_tool_idx').on(t.tool),
    index('ops_pi_dev_tool_calls_status_idx').on(t.status),
    index('ops_pi_dev_tool_calls_created_at_idx').on(t.createdAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_agents — Framework-agnostic agent registry (PR-GOV-01)
// ---------------------------------------------------------------------------
export const opsAgents = pgTable('ops_agents', {
    id: uuid('id').notNull().defaultRandom(),
    // Uniqueness is scoped per-org via the compound `ops_agents_org_agent_id_key`
    // constraint below (MT-RUN-04, NocoDB #4579). Two orgs may legitimately
    // register an agent under the same operator-chosen agent_id.
    agentId: text('agent_id').notNull(),
    organizationId: uuid('organization_id'),
    sdk: text('sdk').notNull(),
    name: text('name').notNull(),
    version: text('version'),
    status: text('status').notNull().default('idle'),
    policy: text('policy').notNull().default('guarded'),
    budgetAction: text('budget_action').notNull().default('warn'),
    budgetMonthly: numeric('budget_monthly', { precision: 10, scale: 2 }),
    budgetPerSession: numeric('budget_per_session', { precision: 10, scale: 2 }),
    // GitHub repo attribution (Sprint 42 S2)
    repoId: integer('repo_id'),
    repoName: text('repo_name'),
    repoFullName: text('repo_full_name'),
    orgId: integer('org_id'),
    budgetSpentThisMonth: numeric('budget_spent_this_month', { precision: 10, scale: 2 }).default('0'),
    config: jsonb('config').default({}),
    webhookUrl: text('webhook_url'),
    webhookSecret: text('webhook_secret'),
    triggers: jsonb('triggers').default([]),
    source: text('source'),
    sourceUrl: text('source_url'),
    metadata: jsonb('metadata').default({}),
    /**
     * Self-reported runtime attestation from the agent process (PIO-31).
     * Populated on every heartbeat. Nullable so runners on pre-PIO-31
     * code keep working — they send no attestation, this stays NULL.
     *
     * Shape (mirrors RunnerAttestation in cli-ceo-agents):
     *   {
     *     repoSha:             string | null,   // git rev-parse HEAD on the agent box
     *     repoOrigin:          string | null,   // remote.origin.url
     *     capabilitiesVersion: string,          // contract version (e.g., 'v1')
     *     secretsHash:         string | null,   // sha256(salt|tokens) — never raw
     *     bootedAtMs:          number,          // when the runner started
     *     nodeVersion:         string,          // process.version
     *   }
     *
     * Drift = (attestation->>'repoSha') != latest cli-ceo-agents/main HEAD.
     * Indexed by (attestation->>'repoSha') for fast fleet-wide drift queries.
     */
    attestation: jsonb('attestation'),
    capabilities: jsonb('capabilities').default({
        sessions: false,
        jobs: false,
        budget: true,
        policy: true,
        rateLimit: false,
        approval: false,
    }),
    rateLimit: jsonb('rate_limit'),
    rateLimitUsed: integer('rate_limit_used').default(0),
    rateLimitWindowStart: timestamp('rate_limit_window_start', { withTimezone: true }),
    lastHeartbeat: timestamp('last_heartbeat', { withTimezone: true }),
    /**
     * Default signing-key kid used by the agent when signing heartbeats
     * (HB-05). NULL = agent never created a signing key (heartbeats land
     * unsigned, accepted unless HEARTBEAT_REQUIRE_SIGNATURE='true'). The
     * full keyring lives in ops_agent_signing_keys; this column is just
     * the convenience pointer to "the most recently created, not-yet-revoked
     * key" so the dashboard can render it without joining.
     */
    signingKeyKid: text('signing_key_kid'),
    /**
     * Pull-verification callback URL (HB-06). When set, the challenge-issuer
     * cron POSTs a signed nonce here every 5 min and expects the agent to
     * sign it back via /api/agents/:agentId/challenge-response. NULL = agent
     * isn't pull-verifiable; rows still get inserted with verdict='no_callback'.
     */
    callbackUrl: text('callback_url'),
    /**
     * Set true after 3 consecutive non-pass challenge results (failed | timeout
     * | no_callback). Cleared on the next pass. Surfaced in the UI as a red
     * banner so moderators see integrity warnings without grepping the
     * challenge-results table.
     */
    challengeFailing: boolean('challenge_failing').notNull().default(false),
    /** Timestamp of the most recent challenge attempt (regardless of verdict). */
    lastChallengeAt: timestamp('last_challenge_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    check('ops_agents_sdk_check', sql `${t.sdk} IN ('pi', 'claude-code', 'anthropic-ma', 'aider', 'custom')`),
    check('ops_agents_status_check', sql `${t.status} IN ('idle', 'running', 'paused', 'terminated')`),
    check('ops_agents_policy_check', sql `${t.policy} IN ('safe', 'guarded', 'manual')`),
    check('ops_agents_budget_action_check', sql `${t.budgetAction} IN ('warn', 'block', 'terminate')`),
    // Compound uniqueness scoping (MT-RUN-04, NocoDB #4579).
    //
    // Replaces the prior global UNIQUE on agent_id with a per-org constraint.
    // NULLS NOT DISTINCT means rows with NULL organization_id (legacy / pre
    // multi-tenancy) still cannot share an agent_id — preserving the old
    // safety net for un-migrated rows. Postgres ≥15 required.
    unique('ops_agents_org_agent_id_key').on(t.organizationId, t.agentId).nullsNotDistinct(),
    index('ops_agents_sdk_idx').on(t.sdk),
    index('ops_agents_status_idx').on(t.status),
    index('ops_agents_last_heartbeat_idx').on(t.lastHeartbeat),
    index('ops_agents_organization_idx').on(t.organizationId),
    // FUS-06 org-ownership activity gate (`readActivityForRuns` in
    // timeline-store.ts). The gate runs TWO correlated subqueries against this
    // table per scanned activity row, BOTH seeking by agent_id:
    //   - ownedByThisOrg:        agent_id = $a AND organization_id  = $org
    //   - notClaimedByAnotherOrg: agent_id = $a AND organization_id != $org
    // The existing UNIQUE `ops_agents_org_agent_id_key` leads with
    // organization_id, so it serves neither seek cleanly — and the `!=` arm of
    // notClaimedByAnotherOrg cannot use an org-leading index at all. This
    // agent_id-LEADING index serves both: seek to the agent_id, then the org
    // eq/ne is satisfied straight from the index (covering, no heap lookup).
    // Complementary to the org-leading UNIQUE, not a duplicate. (drz-006)
    index('ops_agents_agent_org_idx').on(t.agentId, t.organizationId),
]);
/** Runtime-valid agent-type values for Zod schemas and DB check constraints.
 * Extending this set requires a forward migration (MT-RUN-49 §R2).
 * Mirrors `ops_agent_jobs_agent_type_check` in the jobs table.
 */
export const AGENT_TYPE_VALUES = [
    'pr-reviewer',
    'code-reviewer',
    'security-scanner',
    'pr-fixer',
];
// ---------------------------------------------------------------------------
// ops_agent_signing_keys — Per-agent HMAC keys for heartbeat auth (HB-05, NocoDB #4406)
// ---------------------------------------------------------------------------
/**
 * HMAC shared secrets used by `heartbeat-signature` middleware to verify
 * the X-Heartbeat-Signature header on POST /heartbeat and POST /activity.
 *
 * Threat model: server DB compromise lets an attacker forge heartbeats
 * for any agent with a non-revoked key. Mitigation v1 is DB-level
 * encryption-at-rest + role-based access; future migration to
 * asymmetric (Ed25519) would give true non-repudiation. v1 is a
 * meaningful upgrade over the prior "anyone with agent_id can forge"
 * baseline.
 *
 * Lifecycle:
 *   - POST /api/agents/:agentId/keys        → mint a new kid, return secret ONCE
 *   - DELETE /api/agents/:agentId/keys/:kid → revoke (sets revoked_at; row stays for audit)
 *
 * Verification path: middleware loads the row by (agent_id, kid) where
 * revoked_at IS NULL, recomputes HMAC-SHA256 over the canonical request
 * string (timestamp + body), and constant-time-compares with the
 * provided X-Heartbeat-Signature.
 */
export const opsAgentSigningKeys = pgTable('ops_agent_signing_keys', {
    agentId: text('agent_id').notNull(),
    kid: text('kid').notNull(),
    /**
     * HMAC shared secret. Stored as base64url-encoded random bytes. The
     * column name is `secret` rather than `secret_hash` because we cannot
     * hash-and-throw-away an HMAC key — verification needs the original
     * material. See the table comment for the threat model.
     */
    secret: text('secret').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
}, (t) => [
    primaryKey({
        columns: [t.agentId, t.kid],
        name: 'ops_agent_signing_keys_pkey',
    }),
    index('ops_agent_signing_keys_active_idx')
        .on(t.agentId, t.kid)
        .where(sql `${t.revokedAt} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_agent_jobs — Framework-agnostic job queue (PR-GOV-01)
// ---------------------------------------------------------------------------
/**
 * MT-RUN-49 (#4742, migration 0030) — `agent_id` semantic shift.
 * See docs-internal/adr/ADR-010-TENANT-AWARE-WEBHOOK-DISPATCH.md.
 *
 * Tenant-aware dispatch keys on `(organization_id, agent_type)`, NOT on a
 * literal `agent_id`. As a consequence the meaning of `agent_id` on this
 * table changes:
 *
 *   - OLD meaning: the dispatch key. The webhook handler wrote the
 *     capability string ('pr-reviewer' etc.) here, and the claim CTE
 *     matched `WHERE agent_id = <capability>`.
 *   - NEW meaning: the *claimed-by* runner identity. On a webhook-origin
 *     row it is still written as the `agent_type` string at enqueue time
 *     (so `NOT NULL` holds and the legacy claim path keeps working during
 *     the transition), and the claim CTE OVERWRITES it with the claiming
 *     runner's real `agent_id` when the row is claimed.
 *
 * `agent_id` stays `text NOT NULL` for 0030 — it is made nullable, and the
 * enqueue-time write is dropped, in the MT-RUN-42 cleanup task (its own
 * migration). 0030 is purely additive.
 *
 * `agent_type` is the capability/queue the job targets — the real dispatch
 * key. `ops_agent_jobs_dedup_v2_key` is the multi-tenant-correct idempotency
 * scope; the v1 `ops_agent_jobs_dedup_key` is kept through the transition and
 * dropped in the cleanup task.
 */
export const opsAgentJobs = pgTable('ops_agent_jobs', {
    id: text('id').notNull().primaryKey(),
    /**
     * MT-RUN-49: claimed-by runner identity (see table comment). NOT the
     * dispatch key — `agent_type` is. Stays `NOT NULL` for 0030; becomes
     * nullable in the MT-RUN-42 cleanup migration.
     */
    agentId: text('agent_id').notNull(),
    /**
     * MT-RUN-49: the capability/queue the job targets — the dispatch key.
     * Domain matches the webhook's `SUPPORTED_AGENTS`. `DEFAULT 'pr-reviewer'`
     * backfills every existing row (today every webhook-origin row has
     * `agent_id ∈ SUPPORTED_AGENTS`); migration 0030 additionally runs an
     * explicit backfill `UPDATE … SET agent_type = agent_id` for exactness.
     */
    agentType: text('agent_type').notNull().default('pr-reviewer'),
    organizationId: uuid('organization_id'),
    trigger: text('trigger').notNull(),
    triggerSource: text('trigger_source'),
    triggerEventId: text('trigger_event_id'),
    status: text('status').notNull().default('pending'),
    inputPayload: jsonb('input_payload').default({}),
    outputPayload: jsonb('output_payload').default({}),
    error: text('error'),
    cost: numeric('cost', { precision: 10, scale: 6 }),
    sessionId: text('session_id'),
    /**
     * CEO-V4 Phase 0 (NocoDB #4769) — pi-extension session correlation.
     * Optional foreign-style link to the pi-extension session that executed
     * this job (a `pi` runner PATCHes this on session start). Nullable: not
     * every job has a pi-session (legacy jobs, non-pi runners). Indexed for
     * the operator-visibility "active agents" grid which joins jobs ↔ live
     * pi sessions.
     */
    piSessionId: text('pi_session_id'),
    priority: integer('priority').default(0),
    maxRetries: integer('max_retries').default(3),
    retryCount: integer('retry_count').default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
}, (t) => [
    check('ops_agent_jobs_trigger_check', sql `${t.trigger} IN ('webhook', 'api', 'cron', 'manual', 'schedule')`),
    // MT-RUN-49 (0030): `'routing_failed'` added to the set — the terminal
    // state for a job that sat `pending` past its TTL with no eligible
    // runner for its `(organization_id, agent_type)` (ADR-010 R2).
    check('ops_agent_jobs_status_check', sql `${t.status} IN ('pending', 'processing', 'completed', 'failed', 'skipped', 'cancelled', 'routing_failed')`),
    // MT-RUN-49 (0030): `agent_type` domain — the webhook's SUPPORTED_AGENTS.
    // Extending this set as new capabilities ship is itself a forward migration.
    check('ops_agent_jobs_agent_type_check', sql `${t.agentType} IN ('pr-reviewer', 'code-reviewer', 'security-scanner')`),
    index('ops_agent_jobs_status_idx').on(t.status, t.createdAt),
    index('ops_agent_jobs_agent_status_idx').on(t.agentId, t.status, t.createdAt),
    index('ops_agent_jobs_organization_idx').on(t.organizationId),
    // MT-RUN-49 (0030): the new claim hot path. The claim CTE's Path B
    // matches `WHERE organization_id = $org AND agent_type ∈ caps`; this
    // index serves it. The existing `ops_agent_jobs_agent_status_idx` stays
    // for the legacy (Path A) claim path during the transition.
    index('ops_agent_jobs_dispatch_idx').on(t.organizationId, t.agentType, t.status, t.createdAt),
    // CEO-V4 Phase 0 (NocoDB #4769): pi-extension session correlation. Serves
    // the operator-visibility "active agents" grid which filters jobs by the
    // live pi-session id when joining ops_agent_jobs ↔ pi session activity.
    index('ops_agent_jobs_pi_session_idx').on(t.piSessionId),
    // Idempotency scope (MT-RUN-04 follow-on, NocoDB #4579).
    //
    // (agent_id, trigger_source, trigger_event_id) uniqueness, with NULLS
    // NOT DISTINCT so a NULL trigger_source or trigger_event_id still
    // collides — preventing duplicate "manual / no-event-id" runs from
    // the same agent slipping through under multi-tenancy. Postgres ≥15.
    //
    // MT-RUN-49 (0030): KEPT through the transition (see dedup_v2 below).
    // Dropped in the MT-RUN-42 cleanup task once no in-flight code relies
    // on it — NOT dropped here. (Dropping it is MT-RUN-57.)
    unique('ops_agent_jobs_dedup_key')
        .on(t.agentId, t.triggerSource, t.triggerEventId)
        .nullsNotDistinct(),
    // MT-RUN-49 (0030): multi-tenant-correct idempotency scope. The v1 key
    // above was correct only while `agent_id` *was* the capability string.
    // Once `agent_id` becomes claimed-by-runner-identity, re-delivery dedup
    // must key on `(organization_id, agent_type, …)` so (a) it survives the
    // claim-time `agent_id` overwrite and (b) two tenants' jobs with a
    // coincidentally-equal `trigger_event_id` do not dedup against each
    // other. NULLS NOT DISTINCT, Postgres ≥15.
    unique('ops_agent_jobs_dedup_v2_key')
        .on(t.organizationId, t.agentType, t.triggerSource, t.triggerEventId)
        .nullsNotDistinct(),
]);
// ---------------------------------------------------------------------------
// ops_agent_capabilities — runner job-type capability advertisements
// (MT-RUN-49, NocoDB #4742, migration 0030)
// ---------------------------------------------------------------------------
/**
 * Which job *types* (capabilities) a runner can serve. The claim CTE's new
 * (Path B) dispatch matches `WHERE organization_id = $org AND
 * EXISTS (… ops_agent_capabilities … capability = j.agent_type)` — see
 * docs-internal/adr/ADR-010-TENANT-AWARE-WEBHOOK-DISPATCH.md.
 *
 * Why a normalized table, not a JSONB column on `ops_agents`:
 *   - `ops_agents.capabilities` is a fixed boolean shape (`{sessions, jobs,
 *     budget, …}`) describing *transport* capabilities — a different concept
 *     from *job-type* capabilities. `ops_agents.triggers` is an untyped array.
 *     Reusing either would force a `jsonb` containment operator into the
 *     `FOR UPDATE SKIP LOCKED` claim CTE — the hot-path destabilization
 *     ADR-010 explicitly designs against. A narrow table gives the claim CTE
 *     a plain indexed `EXISTS`/`IN`.
 *   - It is the natural home for *heterogeneous* runners (one runner
 *     advertising both `pr-reviewer` and `security-scanner`).
 *
 * `organization_id` is DENORMALIZED here (copied from `ops_agents` at write
 * time) so the claim CTE filters capability + org without a join back to
 * `ops_agents`. There is intentionally NO foreign key: `agent_id` is not
 * unique in `ops_agents` (it is unique only per-org, `UNIQUE (organization_id,
 * agent_id)` — MT-RUN-04), so an FK on `agent_id` alone is not expressible.
 * `ON DELETE` cleanup is handled in the registration / de-registration code
 * paths (code-level cascade), asserted by tests — same pattern as
 * `ops_agent_sessions.actor_id`.
 *
 * Populated by the runner-register flow and `POST /api/agents` (MT-RUN-52).
 */
export const opsAgentCapabilities = pgTable('ops_agent_capabilities', {
    agentId: text('agent_id').notNull(),
    /**
     * Denormalized from `ops_agents.organization_id` at write time. Nullable
     * to mirror `ops_agents` (legacy / super-admin agents may have a NULL
     * org). No FK — see the table comment.
     */
    organizationId: uuid('organization_id'),
    capability: text('capability').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({
        columns: [t.agentId, t.capability],
        name: 'ops_agent_capabilities_pkey',
    }),
    check('ops_agent_capabilities_capability_check', sql `${t.capability} IN ('pr-reviewer', 'code-reviewer', 'security-scanner')`),
    // Claim-time filter: "which of this org's runners serve capability X".
    index('ops_agent_capabilities_org_capability_idx').on(t.organizationId, t.capability),
]);
// ---------------------------------------------------------------------------
// DEPRECATED: ops_pi_dev_agents — Use ops_agents instead
// ---------------------------------------------------------------------------
export const opsPiDevAgents = pgTable('ops_pi_dev_agents', {
    id: uuid('id').notNull().defaultRandom(),
    agentId: text('agent_id').notNull().unique(),
    name: text('name').notNull(),
    source: text('source').notNull(),
    systemPrompt: text('system_prompt'),
    model: text('model'),
    status: text('status').notNull().default('idle'),
    policy: text('policy').notNull().default('guarded'),
    budgetMonthly: numeric('budget_monthly', { precision: 10, scale: 2 }),
    budgetPerSession: numeric('budget_per_session', { precision: 10, scale: 2 }),
    budgetAction: text('budget_action').notNull().default('warn'),
    environment: jsonb('environment'),
    extensions: text('extensions').array(),
    metadata: jsonb('metadata'),
    lastHeartbeat: timestamp('last_heartbeat', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_pi_dev_agents_pkey' }),
    check('ops_pi_dev_agents_status_check', sql `${t.status} IN ('idle', 'running', 'paused', 'terminated')`),
    check('ops_pi_dev_agents_policy_check', sql `${t.policy} IN ('safe', 'guarded', 'manual')`),
    check('ops_pi_dev_agents_budget_action_check', sql `${t.budgetAction} IN ('warn', 'block', 'terminate')`),
    index('ops_pi_dev_agents_status_idx').on(t.status),
    index('ops_pi_dev_agents_last_heartbeat_idx').on(t.lastHeartbeat.desc()),
]);
// ---------------------------------------------------------------------------
// DEPRECATED: pr_review_jobs — Use ops_agent_jobs instead
// ---------------------------------------------------------------------------
export const prReviewJobs = pgTable('pr_review_jobs', {
    id: text('id').notNull().primaryKey(),
    agentId: text('agent_id').notNull().default('pr-reviewer'),
    prNumber: integer('pr_number').notNull(),
    repo: text('repo').notNull(),
    action: text('action').notNull(),
    sender: text('sender').notNull(),
    cloneUrl: text('clone_url').notNull(),
    headRef: text('head_ref').notNull(),
    baseRef: text('base_ref').notNull(),
    title: text('title').notNull(),
    body: text('body'),
    author: text('author').notNull(),
    trigger: text('trigger').notNull().default('pr_event'),
    status: text('status').notNull().default('pending'),
    error: text('error'),
    result: text('result'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
}, (t) => [
    check('pr_review_jobs_status_check', sql `${t.status} IN ('pending', 'processing', 'completed', 'failed', 'skipped')`),
    index('pr_review_jobs_status_idx').on(t.status, t.createdAt),
    index('pr_review_jobs_agent_idx').on(t.agentId, t.createdAt),
]);
// ---------------------------------------------------------------------------
// ops_vcs_repositories — VCS repository to organization mapping (CEO-V3)
// ---------------------------------------------------------------------------
/**
 * Maps VCS repositories to organizations for webhook multi-tenancy.
 * Used by webhook handler to resolve organization context from repository info.
 */
export const opsVcsRepositories = pgTable('ops_vcs_repositories', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    provider: text('provider').notNull(), // 'github', 'gitlab', etc.
    owner: text('owner').notNull(), // repo owner/user
    repo: text('repo').notNull(), // repo name
    fullName: text('full_name').notNull(), // 'owner/repo'
    isDefault: boolean('is_default').notNull().default(false),
    /**
     * Per-repo webhook HMAC secret (MT-RUN-48, NocoDB #4740 — ADR-010 R1).
     *
     * Base64url-encoded 32-byte CSPRNG value, stored in the column rather
     * than `secret_hash` because HMAC-SHA256 verification needs the original
     * material — exactly mirroring `ops_agent_signing_keys.secret`
     * (schema.ts) and its threat model. v1 mitigation is DB-level
     * encryption-at-rest + RBAC; this is NOT an app-level-encrypted or
     * Infisical-managed value, it is tenant DB data.
     *
     * The webhook handler selects THIS row's secret after resolving
     * repo→org from the (untrusted) payload, then constant-time HMAC-verifies
     * before any side effect (`webhooks.ts`, parse-before-verify). NULL means
     * the repo has not been issued a per-repo secret yet → the handler falls
     * back to the global `GITHUB_WEBHOOK_SECRET` (dual-accept) during the
     * migration window (removed in MT-RUN-48 P6 cutover).
     */
    webhookSecret: text('webhook_secret'),
    /**
     * Secondary webhook secret for overlapping-window rotation (MT-RUN-48 P5).
     * During a rotation the handler verifies against `webhook_secret` first,
     * then `webhook_secret_secondary`, so a provider that has not yet been
     * re-pointed at the new secret keeps verifying. Promotion/cleanup is the
     * rotation follow-up task. NULL outside a rotation window.
     */
    webhookSecretSecondary: text('webhook_secret_secondary'),
    /**
     * Timestamp of the most recent webhook-secret rotation (MT-RUN-48 P5).
     * NULL until the secret is first rotated. Drives the ~90-day rotation
     * cadence + drift detection in the rotation phase.
     */
    webhookSecretRotatedAt: timestamp('webhook_secret_rotated_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_vcs_repositories_pkey' }),
    check('ops_vcs_repositories_provider_check', sql `${t.provider} IN ('github', 'gitlab', 'bitbucket', 'gitea', 'forgejo')`),
    uniqueIndex('ops_vcs_repositories_unique_idx').on(t.provider, t.owner, t.repo),
    index('ops_vcs_repositories_organization_idx').on(t.organizationId),
    index('ops_vcs_repositories_full_name_idx').on(t.fullName),
]);
// ---------------------------------------------------------------------------
// ops_agent_heartbeat_events — Append-only heartbeat audit log (HB-01, NocoDB #4402)
// ---------------------------------------------------------------------------
/**
 * Append-only event log for agent heartbeats. Replaces the prior last-write-wins
 * pattern where ops_agents.metadata.lastHeartbeatMeta was overwritten on every
 * POST. This table is the source of truth for governance audit trails:
 * "show me every heartbeat from agent X in the last 7 days", "reconstruct
 * state before incident Y", etc.
 *
 * ops_agents.lastHeartbeat is preserved as a denormalized fast-read shortcut
 * for dashboard list views; this table holds the full history.
 *
 * signature / signatureKid: reserved for HB-05 (HMAC signed heartbeats); NULL
 * until HEARTBEAT_REQUIRE_SIGNATURE strict mode is enabled.
 *
 * source: invocation source vocabulary populated by agent SDKs in HB-07
 * (Paperclip-derived: timer | assignment | on_demand | automation | unknown).
 */
export const opsAgentHeartbeatEvents = pgTable('ops_agent_heartbeat_events', {
    id: uuid('id').notNull().defaultRandom(),
    agentId: text('agent_id').notNull(),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull().defaultNow(),
    source: text('source').notNull().default('unknown'),
    status: text('status').notNull(),
    payload: jsonb('payload').notNull().default({}),
    signature: text('signature'),
    signatureKid: text('signature_kid'),
}, (t) => [
    primaryKey({
        columns: [t.id],
        name: 'ops_agent_heartbeat_events_pkey',
    }),
    check('ops_agent_heartbeat_events_source_check', sql `${t.source} IN ('timer', 'assignment', 'on_demand', 'automation', 'unknown')`),
    check('ops_agent_heartbeat_events_status_check', sql `${t.status} IN ('idle', 'running', 'paused', 'terminated')`),
    index('ops_agent_heartbeat_events_agent_time_idx').on(t.agentId, t.occurredAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_agent_activity — Evidence-of-work events (HB-03, NocoDB #4404)
// ---------------------------------------------------------------------------
/**
 * Lightweight evidence stream that complements ops_agent_heartbeat_events.
 * A heartbeat proves connectivity; an activity row proves productive work.
 *
 * The silent-run watchdog (HB-04) queries this table to flag agents that
 * are pinging fine but have stopped producing output. Moderators see the
 * raw stream on the Activity tab of the agent detail page.
 *
 * Each kind has a distinct payload shape (validated by Zod at the route
 * level — see agent-activity.ts):
 *   tool_call         — { tool, args_summary, result_size?, ms? }
 *   artifact          — { type, uri, title? }
 *   decision          — { summary, rationale?, refs? }
 *   context_compressed — { before_tokens, after_tokens, ratio }
 *   blocker           — { kind, detail }
 *   task_started      — { task_id, title? }
 *   task_completed    — { task_id, outcome? }
 *
 * signature / signatureKid reserved for HB-05; same conventions as
 * ops_agent_heartbeat_events.
 */
export const opsAgentActivity = pgTable('ops_agent_activity', {
    id: uuid('id').notNull().defaultRandom(),
    agentId: text('agent_id').notNull(),
    sessionId: text('session_id'),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull().defaultNow(),
    kind: text('kind').notNull(),
    payload: jsonb('payload').notNull().default({}),
    signature: text('signature'),
    signatureKid: text('signature_kid'),
    /**
     * Client-generated idempotency key (ADR-011 Pillar 1, DUR-05). A globally
     * unique id minted by the sender (UUIDv7 in the durable client) so a
     * retried-after-timeout write resolves to a no-op success instead of a
     * duplicate row. The dedup authority is the partial UNIQUE index below
     * (`ops_agent_activity_client_event_id_key`) enforced *inside* the insert
     * via `ON CONFLICT DO NOTHING` — never a pre-insert SELECT (TOCTOU; see
     * ADR-011 consensus hardening #2).
     *
     * NULLABLE / optional-first by design (migration 0035). The live emitters
     * are raw-`fetch` callers that do not yet send an `eventId`; those legacy
     * writes land with NULL here and coexist (the partial unique index is
     * scoped `WHERE client_event_id IS NOT NULL`, so NULLs never collide).
     * Any sender that *starts* supplying `eventId` immediately gets dedup.
     * Flipping this to NOT NULL + dropping the partial predicate is a later,
     * data-gated step (Phase 5 / DUR-16) once every sender complies.
     *
     * Per ADR-012 Decision 1 this lives ONLY on ops_agent_activity, not on
     * ops_agent_heartbeat_events (heartbeats move to Analytics Engine, so a
     * heartbeat dedup key would be moot).
     */
    clientEventId: text('client_event_id'),
    /**
     * Set when the blocker-alert sweep has fanned out a notification for
     * this row (Slack webhook / email). NULL means "not yet processed".
     * Only `kind='blocker'` rows are ever inspected by the sweep — for any
     * other kind the column stays NULL forever, which is fine. A partial
     * index covers the hot query path.
     *
     * Migration 0034 (CEO-V4 follow-on, Leo "quickest follow-on ship").
     */
    alertedAt: timestamp('alerted_at', { withTimezone: true }),
    /**
     * The cross-feed ArtifactRef the agent DECLARES it touched (ADR-013 D3,
     * FUS-03) — `af1:github:liftping/build-ceo#88`. This is the deterministic
     * write-time correlation primitive: instead of parsing the opaque
     * `payload.uri` string at read time (the trap D3 rejects), an
     * `artifact`-kind row carries a structured, queryable ref. It is the JOIN
     * KEY into the read model — `ops_artifacts` / `ops_contributions` share the
     * same `(organization_id, artifact_ref)` vocabulary — so fusion correlates
     * Feed A (agent) ↔ Feed B (repo) deterministically rather than guessing.
     *
     * NULLABLE / optional-first by design (FUS-03, migration 0042). Legacy
     * events and every non-`artifact` kind (tool_call/decision/…) carry NONE;
     * the still-deployed raw-`fetch` emitters that don't yet send it keep
     * working (the partial index below is scoped `WHERE artifact_ref IS NOT
     * NULL`). It flips to required for `artifact`-kind rows ONLY after every
     * emitter sends it (FUS-08, the ADR-011 DUR-21/E warn-then-reject pattern).
     *
     * Typed `.$type<ArtifactRef>()` so the column carries the branded ref
     * (soft brand — a plain `'af1:…'` literal still assigns; real enforcement
     * is the `isArtifactRef` guard at the write boundary, ADR-013 "validate
     * where written"). NOTE: `ops_agent_activity` itself stays NON-tenant-scoped
     * (ADR-011) — no `organization_id` is added here; the org context for a
     * contribution comes from the run's resolved org at projection time (FUS-04),
     * not from this column. Only the new fusion tables (0039–0041) are
     * tenant-scoped (ADR-013 §"Feed A — ops_agent_activity.artifact_ref").
     */
    artifactRef: text('artifact_ref').$type(),
    /**
     * Canonical episode join key (ADR-011-canonical-episode-id, CEO-Sprint-8).
     * References `ops_episodes.id` — the single unambiguous join key for all
     * archive reads, replacing the ambiguous `session_id` column.
     *
     * NULLABLE / optional-first by design (migration 0051). The write path
     * (worker/archivist) sets this on new inserts; legacy rows and non-archival
     * activity (heartbeats, blocker alerts) carry NULL. The partial index
     * `ops_agent_activity_episode_id_idx` covers the archive read path without
     * penalizing legacy NULL rows.
     *
     * Not a UNIQUE — many activity rows share the same episode (a run produces
     * multiple events). Uniqueness lives on `ops_episodes.id`, not here.
     *
     * Phase progression:
     *   Phase 1 (migration 0051): nullable + index + backfill
     *   Phase 2 (write path): worker sets episode_id on new inserts
     *   Phase 3 (read path): archive G1 uses episode_id instead of session_id
     *   Phase 4: NOT NULL constraint after all phases complete
     */
    episodeId: text('episode_id'),
}, (t) => [
    primaryKey({
        columns: [t.id],
        name: 'ops_agent_activity_pkey',
    }),
    check('ops_agent_activity_kind_check', sql `${t.kind} IN ('tool_call', 'artifact', 'decision', 'context_compressed', 'blocker', 'task_started', 'task_completed')`),
    index('ops_agent_activity_agent_time_idx').on(t.agentId, t.occurredAt.desc()),
    index('ops_agent_activity_kind_idx').on(t.agentId, t.kind, t.occurredAt.desc()),
    // Partial index for the blocker-alert sweep — only the un-alerted
    // blockers are ever scanned. Massively smaller footprint than indexing
    // every row's alerted_at.
    index('ops_agent_activity_blocker_unalerted_idx')
        .on(t.occurredAt)
        .where(sql `${t.kind} = 'blocker' AND ${t.alertedAt} IS NULL`),
    // ADR-011 Pillar 1 (DUR-05) — idempotent-ingest dedup key. Partial
    // (`WHERE client_event_id IS NOT NULL`) because the column is nullable
    // optional-first: legacy NULL rows from raw-fetch senders coexist freely
    // (NULLs are never deduped against), while any supplied client_event_id is
    // globally unique. A bare UNIQUE (not `(agent_id, client_event_id)`)
    // because a UUIDv7 is already globally unique (ADR-011: tightening to a
    // compound key is a forward migration if a client ever emits non-unique
    // ids — noted, not pre-built).
    uniqueIndex('ops_agent_activity_client_event_id_key')
        .on(t.clientEventId)
        .where(sql `${t.clientEventId} IS NOT NULL`),
    // FUS-03 fusion lookup — "what agent activity declared artifact X" (ADR-013
    // D3). Partial (`WHERE artifact_ref IS NOT NULL`) because the column is
    // nullable optional-first: only `artifact`-kind rows that DECLARE a ref are
    // ever indexed, so legacy NULL rows and every non-artifact kind add no
    // index footprint. NOT a UNIQUE — many activity rows can reference the same
    // artifact (multiple touches across a run); uniqueness lives on
    // `ops_artifacts (organization_id, artifact_ref)`, not on the raw feed.
    index('ops_agent_activity_artifact_ref_idx')
        .on(t.artifactRef)
        .where(sql `${t.artifactRef} IS NOT NULL`),
    // FUS-06 run-narrowing for the unified-timeline read (`readActivityForRuns`
    // in timeline-store.ts). The session-scoped match `inArray(session_id,
    // runIds)` narrows a page's activity to its `agent_emitted` runs; without
    // an index that is a seq-scan of the whole feed per page. Partial
    // (`WHERE session_id IS NOT NULL`) mirrors the query exactly — the
    // session-scoped arm only ever matches non-NULL session_ids (the NULL arm
    // is a separate `agent_id` fallback served by the agent-leading indexes
    // above), so legacy/session-less rows add no index footprint. NOT a UNIQUE
    // — many activity rows share a session_id (multiple events per run.) (drz-006)
    index('ops_agent_activity_session_id_idx')
        .on(t.sessionId)
        .where(sql `${t.sessionId} IS NOT NULL`),
    // CEO-Sprint-8 — canonical episode join key (ADR-011-canonical-episode-id).
    // Partial (`WHERE episode_id IS NOT NULL`) mirrors the archive G1 read path
    // exactly: only rows that belong to a known episode are ever scanned by
    // `GET /api/agents/:agentId/episodes/:episodeId`. Legacy rows with NULL
    // coexist freely (NULL never matches `= episodeId` in the read query).
    // NOT a UNIQUE — many activity rows share an episode (one run, many events).
    index('ops_agent_activity_episode_id_idx')
        .on(t.episodeId)
        .where(sql `${t.episodeId} IS NOT NULL`),
]);
// ---------------------------------------------------------------------------
// ops_vcs_events — Feed B, first-class append-only VCS evidence (FUS-01,
// ADR-013 §"0037 — ops_vcs_events")
// ---------------------------------------------------------------------------
/**
 * The repository-side feed of the unified "how software gets built" timeline
 * (ADR-013 Feed B). Today the verified webhook path parses the full PR/issue
 * payload and then DISCARDS it — only an `ops_agent_jobs` dispatch row is
 * created, with the VCS fields buried in its `inputPayload` jsonb where they
 * are not queryable, replayable, or correlatable (ADR-013 Context, D1). This
 * table makes each VCS event first-class, queryable, append-only evidence.
 *
 * It is an ADDITIONAL persist alongside `queueReviewJob` — the ADR-010
 * job-creation path is unchanged (ADR-013 §"Why a new column…"). The webhook
 * handler writes here AFTER signature verification + org resolution, never
 * before (MT-RUN-48 parse-before-verify; persisting a forged webhook as
 * first-class evidence would amplify the blast radius — ADR-013 R2).
 *
 * Append-only: rows are inserted, never updated. The projection to
 * `ops_artifacts`/`ops_contributions` (the read model) is FUS-02+, NOT built
 * here — this task is only the table + the persist + dedup.
 *
 * TENANT-SCOPED (ADR-013 D6 / `org-001`/`org-002`) — diverges from the
 * non-tenant-scoped ADR-011 spine tables (`ops_agent_activity`,
 * `ops_agent_heartbeat_events`). Fusing a private agent transcript with public
 * repo data is a leakage vector, so every fusion row carries
 * `organization_id` and a public artifact two tenants both touch is two rows,
 * never one shared row.
 *
 * Idempotency reuses the ADR-011 DUR-05 pattern (NOT a second dedup scheme):
 * `client_event_id` carries the provider delivery id (GitHub
 * `X-GitHub-Delivery`, stable across the provider's own retries — the same id
 * threaded into `ops_agent_jobs.trigger_event_id`), and the partial UNIQUE
 * below is enforced inside the insert via `ON CONFLICT DO NOTHING`, so a
 * redelivered webhook collapses to a no-op (one row).
 *
 * `seq` reuses the DUR-11 monotonic-cursor shape (`GENERATED ALWAYS AS
 * IDENTITY`) so the future unified-timeline read (FUS-06) can page off
 * `(organization_id, seq)`; it is a per-table identity here.
 *
 * `payload` is offload-ready (ADR-013 R5 / DUR-13): large payloads move to R2
 * with a pointer column at corpus scale; phase 1 keeps it inline (small at
 * current volumes) and the compound `(organization_id, seq)` index keeps the
 * timeline read cheap.
 */
export const opsVcsEvents = pgTable('ops_vcs_events', {
    id: uuid('id').notNull().defaultRandom(),
    /** Tenant scope (ADR-013 D6). NOT NULL — the webhook resolves org before persist. */
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    /**
     * Idempotency key (ADR-011 DUR-05). Carries the provider delivery id
     * (`<provider>:<X-GitHub-Delivery>`) so a redelivered webhook dedups via
     * the UNIQUE below + `ON CONFLICT DO NOTHING`. NOT NULL here (unlike the
     * optional-first column on `ops_agent_activity`) because the webhook
     * persist path always synthesizes a key — the provider delivery id when
     * present, else a deterministic per-event fallback.
     */
    clientEventId: text('client_event_id').notNull(),
    /** DUR-11 monotonic cursor shape — per-table identity; the timeline read path. */
    seq: bigint('seq', { mode: 'number' }).generatedAlwaysAsIdentity(),
    /** 'github' | 'gitlab' | … — same provider vocabulary as ops_vcs_repositories. */
    provider: text('provider').notNull(),
    /** 'pull_request' | 'pull_request_comment' | 'push' | 'issue' | 'branch' … */
    eventType: text('event_type').notNull(),
    /**
     * Payload schema version (ADR-013 consensus: payload evolution is
     * first-class, not only the ArtifactRef version prefix). Defaults to 1.
     */
    eventVersion: smallint('event_version').notNull().default(1),
    /**
     * GitHub Global Node ID — stored OPAQUE (ADR-013 §"ArtifactRef"). Identity
     * when present; never parsed, never forces a synchronous GraphQL hop. The
     * webhook payload already carries it. NULL when the event has no node_id.
     */
    nodeId: text('node_id'),
    /**
     * The cross-feed key (ADR-013 D2). Readable anchor with an `af1:` version
     * prefix, e.g. `af1:github:liftping/build-ceo#88`. Forward-migratable
     * without rewriting stored rows.
     */
    artifactRef: text('artifact_ref').notNull(),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull().defaultNow(),
    /** The parsed VCS payload (today discarded after the job is enqueued). */
    payload: jsonb('payload').notNull().default({}),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_vcs_events_pkey' }),
    check('ops_vcs_events_provider_check', sql `${t.provider} IN ('github', 'gitlab', 'bitbucket', 'gitea', 'forgejo')`),
    // DUR-05 dedup authority. A bare UNIQUE (not compound with org) because the
    // provider delivery id is already globally unique — a webhook retry carries
    // the SAME delivery id regardless of org, so deduping on it alone is
    // correct and a redelivery collapses to one row via ON CONFLICT DO NOTHING.
    uniqueIndex('ops_vcs_events_client_event_id_key').on(t.clientEventId),
    // The timeline read path (ADR-013 §"compound (organization_id, seq)").
    index('ops_vcs_events_org_seq_idx').on(t.organizationId, t.seq),
    // Fusion lookup by cross-feed key, org-scoped (tenant isolation, D6).
    index('ops_vcs_events_org_artifact_ref_idx').on(t.organizationId, t.artifactRef),
]);
// ---------------------------------------------------------------------------
// ops_artifacts — the normalized read-model entity (FUS-02,
// ADR-013 §"0038 — ops_artifacts")
// ---------------------------------------------------------------------------
/**
 * Materialized current-state of one artifact (a PR / issue / commit / branch)
 * on the unified "how software gets built" timeline (ADR-013 Option 3,
 * CQRS-lite). This is the queryable READ MODEL: `ops_vcs_events` (Feed B) and
 * `ops_agent_activity` (Feed A) are the append-only source-of-record; this
 * table is the projection FUS-04 will upsert INTO at write time (synchronous,
 * in-transaction — NOT an async projector; ADR-013 consensus hardening #2).
 *
 * FUS-02 builds the TABLE + minimal read accessors only. The upsert
 * (current-state materialization) is FUS-04 and is NOT built here — but the
 * shape is defined to support it: `(organization_id, artifact_ref)` is the
 * upsert conflict target, and `first_seen_seq`/`last_seen_seq` are the
 * monotonic bookkeeping a late/out-of-order event updates rather than
 * duplicates (ADR-013 R4).
 *
 * TENANT-SCOPED (ADR-013 D6 / `org-001`/`org-002`) — exactly mirroring
 * `ops_vcs_events` (FUS-01) and diverging from the non-tenant-scoped ADR-011
 * spine tables. The UNIQUE is `(organization_id, artifact_ref)`, NOT a global
 * unique on `artifact_ref`: a public PR two tenants both touch is TWO artifact
 * rows (one per org), never one shared row (ADR-013 consensus hardening #4).
 * `node_id`/`artifact_ref` is shared *identity vocabulary*; the row and its
 * metadata are tenant-private. No cross-tenant join is expressible.
 *
 * `kind` reuses the ArtifactKind vocabulary (`src/vcs/artifact-ref.ts`):
 * 'pull_request' | 'commit' | 'issue' | 'branch'. `node_id` is the GitHub
 * Global Node ID, stored OPAQUE (identity when known; never parsed).
 */
export const opsArtifacts = pgTable('ops_artifacts', {
    id: uuid('id').notNull().defaultRandom(),
    /** Tenant scope (ADR-013 D6). NOT NULL — projection resolves org before upsert. */
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    /**
     * The cross-feed key (ADR-013 D2) — the `af1:`-prefixed readable anchor,
     * e.g. `af1:github:liftping/build-ceo#88`. The upsert conflict target
     * (with `organization_id`); forward-migratable without rewriting rows.
     */
    artifactRef: text('artifact_ref').notNull(),
    /**
     * GitHub Global Node ID — stored OPAQUE (ADR-013 §"ArtifactRef"). Identity
     * when known; never parsed. NULL until a Feed-B event carrying it lands.
     */
    nodeId: text('node_id'),
    /**
     * 'github' | 'gitlab' | … — same provider vocabulary as ops_vcs_events.
     * `.$type<VcsProviderName>()` mirrors the `ops_artifacts_provider_check`
     * CHECK at the TS boundary (FUS-02 review nit), so an out-of-vocab provider
     * is a compile error — making the `@ts-expect-error` in the integration
     * test fire — not only a runtime 23514.
     */
    provider: text('provider').$type().notNull(),
    /**
     * ArtifactKind: 'pull_request' | 'commit' | 'issue' | 'branch'.
     * `.$type<ArtifactKind>()` mirrors the `ops_artifacts_kind_check` CHECK at
     * the TS boundary (FUS-02 review nit): an out-of-vocab kind (e.g.
     * 'merge_request') is rejected at compile time, so the negative
     * type-assertion in the integration test is live.
     */
    kind: text('kind').$type().notNull(),
    title: text('title'),
    url: text('url'),
    /**
     * The `seq` of the first source event that materialized this artifact, and
     * the most recent one — the monotonic bookkeeping that makes the FUS-04
     * upsert replay-safe under out-of-order/backfill arrival (ADR-013 R4). A
     * late event updates `last_seen_seq` rather than inserting a duplicate row.
     */
    firstSeenSeq: bigint('first_seen_seq', { mode: 'number' }).notNull(),
    lastSeenSeq: bigint('last_seen_seq', { mode: 'number' }).notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_artifacts_pkey' }),
    check('ops_artifacts_provider_check', sql `${t.provider} IN ('github', 'gitlab', 'bitbucket', 'gitea', 'forgejo')`),
    // ArtifactKind vocabulary (src/vcs/artifact-ref.ts) — canonical, do not
    // invent kinds outside this set (ts-004).
    check('ops_artifacts_kind_check', sql `${t.kind} IN ('pull_request', 'commit', 'issue', 'branch')`),
    // Tenant-scoped uniqueness (ADR-013 D6 / hardening #4): the FUS-04 upsert
    // conflict target. A public artifact two tenants touch is two rows, one per
    // org — NOT a global unique on artifact_ref. No shared cross-tenant row.
    uniqueIndex('ops_artifacts_org_ref_key').on(t.organizationId, t.artifactRef),
    // Org-scoped kind filter for the timeline read (FUS-06).
    index('ops_artifacts_org_kind_idx').on(t.organizationId, t.kind),
    // FUS-06 unified-timeline read path: the artifact spine pages forward on
    // `last_seen_seq` (the artifact's position on the DUR-11 seq cursor),
    // org-scoped. This compound index backs `ORDER BY last_seen_seq` +
    // `WHERE organization_id = ? AND last_seen_seq > ?` so the seq-cursor
    // pagination is an index range scan, not a per-org seq-scan as the corpus
    // grows (ADR-013 §"UNIFIED TIMELINE — query off seq, org-scoped"). Migration
    // 0044, additive.
    index('ops_artifacts_org_seq_idx').on(t.organizationId, t.lastSeenSeq),
]);
// ---------------------------------------------------------------------------
// ops_contributions — correlation, SEPARATE from artifact metadata (FUS-02,
// ADR-013 §"0039 — ops_contributions")
// ---------------------------------------------------------------------------
/**
 * Per-actor contributions to an artifact — "agent run X claims it created PR
 * #88" — kept in a SEPARATE table from `ops_artifacts` metadata (ADR-013 D3,
 * both consensus models). WHY separate: correlation confidence/status CHURNS
 * (pending→verified, temporal corrections via `valid_to`); co-locating it with
 * artifact metadata forces repeated updates to a hot row — the ~40%
 * ingest-throughput regression a peer hit. The separation is the proven shape
 * ("GitHub separates commit objects from ref logs").
 *
 * Related to `ops_artifacts` by the `(organization_id, artifact_ref)` key
 * rather than an artifact-row FK: a contribution can be recorded for an
 * artifact_ref before/independently of the artifact row existing (Feed A may
 * declare a ref the projection materializes moments later, out of order —
 * ADR-013 R4), so a hard FK to `ops_artifacts.id` would impose an ordering the
 * CQRS-lite model intentionally does not require. Both tables are tenant-scoped
 * on the same `(organization_id, artifact_ref)` vocabulary; tenant isolation is
 * enforced by `organization_id` on every row (D6).
 *
 * FUS-02 builds the TABLE + minimal read accessors only. The write-time insert
 * (deterministic correlation: status='verified', method='agent_emitted',
 * confidence=1.0) is FUS-04; the reconciliation sweep (pending→verified/
 * rejected) is FUS-05 — NEITHER is built here.
 *
 * `confidence`/`method` are present but trivially-used in phase 1
 * (deterministic emission = 1.0 / 'agent_emitted'). They exist so the future
 * confidence-weighted resolution graph (GitHub-id ~0.95, text mentions ~0.65)
 * adopts WITHOUT a migration — design-in, not build (ADR-013 D5).
 * `status` is the canonical vocabulary: pending|verified|rejected — do NOT
 * invent statuses outside this set (ts-004).
 */
export const opsContributions = pgTable('ops_contributions', {
    id: uuid('id').notNull().defaultRandom(),
    /** Tenant scope (ADR-013 D6). NOT NULL — projection resolves org before insert. */
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    /** The agent run / session that claims the artifact (Feed A). */
    runId: text('run_id').notNull(),
    /**
     * The cross-feed key relating this contribution to its artifact — the same
     * `af1:`-prefixed anchor as `ops_artifacts.artifact_ref`. Key-related (not
     * a row FK) so a claim can precede the materialized artifact row (R4).
     */
    artifactRef: text('artifact_ref').notNull(),
    /**
     * 0.0–1.0 confidence in the correlation. Phase 1 deterministic emission is
     * 1.0; the future confidence-weighted graph uses ~0.95 (GitHub id) /
     * ~0.65 (text mention) — design-in, not built here (D5).
     */
    confidence: real('confidence').notNull().default(1.0),
    /**
     * How the correlation was established: 'agent_emitted' (Feed A declares a
     * structured ref — phase 1), 'webhook_reconciled' (the reconciliation sweep
     * resolved it — FUS-05), 'text_mention' (future ML matcher — deferred).
     * `.$type<>()` mirrors the `ops_contributions_method_check` CHECK at the TS
     * boundary (FUS-02 review nit) — an out-of-vocab method is a compile error.
     */
    method: text('method')
        .$type()
        .notNull(),
    /**
     * Canonical correlation status (ts-004): pending | verified | rejected.
     * `.$type<>()` mirrors the `ops_contributions_status_check` CHECK at the TS
     * boundary (FUS-02 review nit) so the negative type-assertion in the
     * integration test fires — an out-of-vocab status is rejected at compile
     * time, not only by the runtime CHECK.
     */
    status: text('status')
        .$type()
        .notNull()
        .default('pending'),
    /** Temporal validity: when the correlation became valid. */
    validFrom: timestamp('valid_from', { withTimezone: true }).notNull().defaultNow(),
    /** NULL = currently valid; set on temporal correction (supersession). */
    validTo: timestamp('valid_to', { withTimezone: true }),
    /**
     * The governance rule-set version active when this contribution was
     * recorded. Enables the provenance timeline to show not just WHAT
     * happened but under WHICH rules it was evaluated. Nullable — existing
     * rows pre-date the field; callers pass the version from the compliance
     * ruleset identifier (e.g. 'compliance-rules@2026-05-22').
     */
    ruleSetVersion: text('rule_set_version'),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_contributions_pkey' }),
    // Canonical correlation status vocabulary (ADR-013 §3 / ts-004) — the
    // reconciliation loop (FUS-05) only ever moves a row within this set.
    check('ops_contributions_status_check', sql `${t.status} IN ('pending', 'verified', 'rejected')`),
    // Method vocabulary — 'agent_emitted'/'webhook_reconciled' in phase 1,
    // 'text_mention' reserved for the deferred ML matcher (ADR-013 D5).
    check('ops_contributions_method_check', sql `${t.method} IN ('agent_emitted', 'webhook_reconciled', 'text_mention')`),
    // Confidence is a probability — bound it so a bad write fails loud.
    check('ops_contributions_confidence_check', sql `${t.confidence} >= 0 AND ${t.confidence} <= 1`),
    // Fusion lookup: "who contributed to artifact X", org-scoped (D6). The
    // artifacts↔contributions relationship read path (FUS-06).
    index('ops_contributions_org_artifact_ref_idx').on(t.organizationId, t.artifactRef),
    // "What did run R contribute", org-scoped (D6).
    index('ops_contributions_org_run_id_idx').on(t.organizationId, t.runId),
    // FUS-04 idempotency authority (migration 0043). The deterministic
    // write-time projection inserts one contribution per
    // (org, run_id, artifact_ref, method) with `ON CONFLICT DO NOTHING`; this
    // partial UNIQUE is its conflict target, so re-projecting the SAME source
    // event (a webhook redelivery / activity retry that slips past the
    // upstream row-count gate) is a DB-ENFORCED no-op rather than a duplicate
    // correlation row. PARTIAL on `WHERE valid_to IS NULL` so it constrains
    // only the CURRENTLY-VALID contribution: the temporal-correction model
    // (ADR-013 §3 — supersede by setting `valid_to`, then insert a new row)
    // still works, because a superseded row (valid_to set) leaves the partial
    // index, freeing the slot for its replacement. `method` is in the key so a
    // webhook-reconciled contribution and an agent-emitted one for the same
    // (run, artifact) are distinct rows (the D3 dual-source correlation), not a
    // collision.
    uniqueIndex('ops_contributions_dedup_key')
        .on(t.organizationId, t.runId, t.artifactRef, t.method)
        .where(sql `${t.validTo} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_agent_events — Feed-A canonical transcript index (FUS-AI-01,
// ADR-014 §"0041 — ops_agent_events")
// ---------------------------------------------------------------------------
/**
 * The source-agnostic CANONICAL Feed-A event index (ADR-014). Every agent
 * runtime (Claude Code / pi / opencode / codex) normalizes its heterogeneous
 * transcript shape into the one `CanonicalEvent` grain (see
 * `ingest/feed-a/canonical-event.ts`) and lands HERE — so the ADR-013 fused
 * timeline never sees per-source shape (ADR-014 D1). The verbatim source rows
 * and any large/cold content live in R2 (content-addressed); this table is the
 * lean, queryable INDEX + the cross-feed key + the `seq` spine (the
 * index-in-Postgres / blobs-in-R2 split, `INFRASTRUCTURE_DATA_LAYER.md`; an
 * 18 MB Claude session proves it is needed from day one).
 *
 * FUS-AI-01 builds the TABLE + the read accessors only. The per-source ADAPTERS
 * (FUS-AI-02/06/08/09), the INGEST ROUTE (FUS-AI-05, `onConflictDoNothing` on
 * `event_id` + the reject-truncated-without-blob_uri rule), and the PROJECTION
 * into ADR-013 `ops_contributions`/`ops_vcs_events` (FUS-AI-04) are NOT built
 * here — this table is the contract they bind to.
 *
 * Why a NEW table, not extend `ops_agent_activity` (ADR-014 §"Why a new
 * table"): `ops_agent_activity` (ADR-011) is the agent-emitted LIVE activity
 * stream (heartbeats, declared artifacts) and stays non-tenant-scoped.
 * `ops_agent_events` is the canonical normalized TRANSCRIPT — a different grain
 * (per transcript event, multi-source, tenant-scoped) with the `kind` taxonomy
 * and the R2-pointer columns. It PROJECTS INTO ADR-013's read model; it does
 * not replace `ops_agent_activity`. Whether the two later converge is a
 * follow-up, not this task.
 *
 * TENANT-SCOPED (ADR-014 D6 / R1, converging with the ADR-013 fusion tables and
 * diverging from the non-tenant-scoped ADR-011 spine): a fused private
 * transcript is a leakage vector (reasoning content is the sharpest edge), so
 * `organization_id NOT NULL` + FK CASCADE on every row, `org-001`/`org-002`. A
 * public artifact two tenants both touch is two rows, never one shared row.
 *
 * Idempotency reuses the ADR-011 DUR-05 pattern (NOT a second dedup scheme):
 * `event_id` IS the `client_event_id`, minted DETERMINISTICALLY by the local
 * capture (ADR-015) so a re-scan/re-upload reproduces the SAME id (C2). The
 * bare `UNIQUE (event_id)` below is the `onConflictDoNothing` target the ingest
 * route (FUS-AI-05) keys on — a redelivery collapses to one row. It is NOT
 * compound with `organization_id`: the id is globally unique by construction
 * (a UUIDv5/v7), so deduping on it alone is correct (same rationale as
 * `ops_vcs_events_client_event_id_key`).
 *
 * `seq` reuses the DUR-11 monotonic-cursor shape (`GENERATED ALWAYS AS
 * IDENTITY`) — SERVER-ASSIGNED ONLY (C3). The source's native ordering
 * (opencode `event_sequence.seq`, JSONL row index) lives in the SEPARATE
 * nullable `source_ordinal`; it is NEVER injected into this identity column
 * (overriding `GENERATED ALWAYS` desyncs the counter and collides cross-source).
 * The fused timeline still sorts by the server `seq`.
 *
 * Cost (ADR-014 D7 / C6): `usage` is stored VERBATIM (the field names differ
 * per provider on purpose); the normalized `cost_micros` carries `cost_source ∈
 * {provider_reported, derived}` and, when `derived`, a `pricing_version`
 * pinning the rate table used. Mixing real (pi USD) and derived (Claude tokens)
 * cost in one baked field would let a later rate-table fix silently rewrite
 * billing history — so they are separate, audit-able lanes.
 *
 * The closed CHECK sets (`source` / `actor` / `kind` / `cost_source`) MIRROR
 * the consts in `ingest/feed-a/canonical-event.ts` by hand — the same
 * const-drives-CHECK discipline `ARTIFACT_KINDS` / `ops_artifacts_kind_check`
 * use. `kind` is the closed 12 with `system_event` as the no-drop escape hatch
 * (its `payload.subtype` carries the precise source event; C5).
 *
 * @see docs-internal/adr/ADR-014-FEED-A-CANONICAL-INGEST.md
 * @see packages/core/src/ingest/feed-a/canonical-event.ts — the wire/parse contract
 * @see packages/core/src/db/agent-events-store.ts — the FUS-AI-01 read accessors
 */
export const opsAgentEvents = pgTable('ops_agent_events', {
    id: uuid('id').notNull().defaultRandom(),
    /**
     * Tenant scope (ADR-014 D6). NOT NULL + FK CASCADE — the ingest route
     * resolves the run's org before persist; a fused private transcript is a
     * leakage vector so there is no NULL-org lane here.
     */
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    /**
     * = ADR-011 `client_event_id` (DUR-05); THE idempotency key. Minted
     * deterministically by the local capture (ADR-015) so re-uploads dedup via
     * the bare UNIQUE below + `ON CONFLICT DO NOTHING`. NOT a new key — the
     * same discipline as `ops_agent_activity_client_event_id_key`.
     */
    eventId: text('event_id').notNull(),
    /** DUR-11 monotonic cursor shape — per-table identity, server-only (C3). */
    seq: bigint('seq', { mode: 'number' }).generatedAlwaysAsIdentity(),
    /**
     * Originating runtime — 'claude_code' | 'pi' | 'opencode' | 'codex'.
     * CHECK-mirrors `CANONICAL_SOURCES`. `.$type<>()` documents the closed set
     * at the TS boundary so a bad value is a compile error, not only a 23514.
     */
    source: text('source').$type().notNull(),
    /** Adapter/schema version of the source format (forward-migratable). */
    sourceVersion: text('source_version').notNull().default('1'),
    /** Canonical session id (the source session/uuid). */
    sessionId: text('session_id').notNull(),
    /** Subagent → parent session (pi subagent-artifacts; Claude `isSidechain`). */
    parentSessionId: text('parent_session_id'),
    /** Intra-session causal parent (assistant turn → its tool calls). */
    parentId: text('parent_id'),
    /** `tool_call` ↔ `tool_result` correlation key (C4); stable within session. */
    callId: text('call_id'),
    /**
     * Native source order (C3) — opencode `event_sequence.seq` / JSONL row
     * index. The ONLY place a source-native sequence is stored; NEVER the
     * server `seq`. Nullable (a source may have no native ordinal).
     */
    sourceOrdinal: bigint('source_ordinal', { mode: 'number' }),
    /** When the event occurred at the source. */
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull().defaultNow(),
    /** Optional event duration (ms). */
    durationMs: integer('duration_ms'),
    /**
     * Who/what produced it. CHECK-mirrors `CANONICAL_ACTORS`
     * ('user'|'assistant'|'tool'|'system'|'subagent').
     */
    actor: text('actor').$type().notNull(),
    /**
     * The closed taxonomy. CHECK-mirrors `CANONICAL_KINDS` (the 12);
     * `system_event` is the no-drop escape hatch (`payload.subtype`, C5).
     */
    kind: text('kind').$type().notNull(),
    /** PER-EVENT model (pi is multi-model in one session; `model_change` → system_event). */
    model: text('model'),
    /** Verbatim source usage (D7) — tokens, and pi's `cost` USD; stored as-is. */
    usage: jsonb('usage'),
    /** Normalized cost in micros (1e-6 USD); paired with `cost_source`. Nullable. */
    costMicros: bigint('cost_micros', { mode: 'number' }),
    /**
     * Provenance of `cost_micros`. CHECK-mirrors `COST_SOURCES`
     * ('provider_reported'|'derived'); nullable when no cost is recorded.
     */
    costSource: text('cost_source').$type(),
    /** Pinned rate-table version when `cost_source='derived'` (C6); else NULL. */
    pricingVersion: text('pricing_version'),
    /** Per-event git `{ branch, cwd }`, or a session-derived fallback. */
    git: jsonb('git'),
    /**
     * The ADR-013 cross-feed key (`af1:…`) on an `artifact_link` event — the
     * seam FUS-AI-04 reads to project into `ops_contributions`. Reuses the
     * ADR-013 `ArtifactRef` type (SOFT brand: a plain `af1:` literal still
     * assigns); do NOT define a second scheme. Nullable (most events carry none).
     */
    artifactRef: text('artifact_ref').$type(),
    /** The small/structured remainder (+ `subtype` for `system_event`). */
    payload: jsonb('payload').notNull().default({}),
    /** R2 pointer when content exceeded the inline cap or was offloaded (C1/C2). */
    blobUri: text('blob_uri'),
    /** R2 pointer to the verbatim source row (ALWAYS — replay/audit source). */
    rawUri: text('raw_uri').notNull(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_agent_events_pkey' }),
    // DUR-05 dedup authority — the `onConflictDoNothing` target (FUS-AI-05).
    // Bare UNIQUE (not compound with org): the deterministic UUIDv5/v7
    // `event_id` is globally unique by construction, so a re-upload carries the
    // SAME id regardless of org and collapses to one row. Mirrors
    // `ops_vcs_events_client_event_id_key`.
    uniqueIndex('ops_agent_events_event_id_key').on(t.eventId),
    // The fused-timeline read path (ADR-014 §"0041"; ADR-013 compound cursor).
    index('ops_agent_events_org_seq_idx').on(t.organizationId, t.seq),
    // In-session replay/playback by native order (C3), org-scoped.
    index('ops_agent_events_org_session_ordinal_idx').on(t.organizationId, t.sessionId, t.sourceOrdinal),
    // ADR-013 fusion lookup by cross-feed key, org-scoped (partial — most
    // events carry no ref). The FUS-AI-04 projection reads this.
    index('ops_agent_events_org_artifact_ref_idx')
        .on(t.organizationId, t.artifactRef)
        .where(sql `${t.artifactRef} IS NOT NULL`),
    // tool_call ↔ tool_result pairing (C4), org-scoped (partial — most events
    // carry no call_id). An orphaned tool_result is a metric, not a gap.
    index('ops_agent_events_org_call_id_idx')
        .on(t.organizationId, t.callId)
        .where(sql `${t.callId} IS NOT NULL`),
    check('ops_agent_events_source_check', sql `${t.source} IN ('claude_code', 'pi', 'opencode', 'codex')`),
    check('ops_agent_events_actor_check', sql `${t.actor} IN ('user', 'assistant', 'tool', 'system', 'subagent')`),
    check('ops_agent_events_kind_check', sql `${t.kind} IN ('session_start', 'user_message', 'agent_message', 'reasoning', 'tool_call', 'tool_result', 'file_change', 'file_snapshot', 'artifact_link', 'permission_decision', 'subagent_run', 'system_event')`),
    check('ops_agent_events_cost_source_check', sql `${t.costSource} IS NULL OR ${t.costSource} IN ('provider_reported', 'derived')`),
]);
// ---------------------------------------------------------------------------
// ops_token_rates — Versioned model pricing for auditable cost derivation
// (FUS-AI-03, ADR-014 C6/D7)
// ---------------------------------------------------------------------------
//
// The versioned token-rate table that enables reproducible, auditable
// `derived` cost on `ops_agent_events`. When `cost_source = 'derived'`,
// the `pricing_version` column on the event pinpoints the exact rate
// snapshot used — so a later rate-table fix never silently rewrites
// billing history (ADR-014 D7/C6).
//
// GLOBAL (NOT tenant-scoped) — model pricing is platform-wide, not
// per-org. Two tenants deriving cost for the same model at the same
// time use the same rate row. No `organization_id` column.
//
// Each `pricing_version` is a semver-style or date-stamped label
// (e.g. 'rate-2026-05-22', 'rate-v2') that groups one snapshot of
// every model's rates. Rows within a version are immutable once
// published — superseded by creating a new version, never by editing
// in place. `effective_from` / `effective_until` define the validity
// window for audit display; NULL `effective_until` means "still
// active".
//
// Aligns with the existing `MODEL_RATES` constant in
// `shared-constants/src/model-rates.ts` — that constant is the
// runtime convenience copy; this table is the durable, auditable
// source of truth for derived cost.
//
// @see docs-internal/adr/ADR-014-FEED-A-CANONICAL-INGEST.md §C6/D7
// @see packages/shared-constants/src/model-rates.ts
export const opsTokenRates = pgTable('ops_token_rates', {
    id: uuid('id').notNull().defaultRandom(),
    /**
     * Version identifier that groups one snapshot of all model rates.
     * e.g. 'rate-2026-05-22', 'rate-v2'. Pinned on
     * `ops_agent_events.pricing_version` when `cost_source='derived'`.
     */
    pricingVersion: text('pricing_version').notNull(),
    /**
     * The model this rate applies to. Matches the model name as it
     * appears on `ops_agent_events.model` (e.g. 'claude-opus-4-6',
     * 'minimax', 'gpt-5.2').
     */
    model: text('model').notNull(),
    /** Input token rate, USD per 1M tokens. */
    inputPer1m: numeric('input_per_1m', { precision: 12, scale: 6 }).notNull(),
    /** Output token rate, USD per 1M tokens. */
    outputPer1m: numeric('output_per_1m', { precision: 12, scale: 6 }).notNull(),
    /** Cache write (5-min TTL) rate, USD per 1M tokens. Nullable if N/A for provider. */
    cacheWrite5mPer1m: numeric('cache_write_5m_per_1m', { precision: 12, scale: 6 }),
    /** Cache write (1-hr TTL) rate, USD per 1M tokens. Nullable if N/A for provider. */
    cacheWrite1hPer1m: numeric('cache_write_1h_per_1m', { precision: 12, scale: 6 }),
    /** Cache read rate, USD per 1M tokens. Nullable if N/A for provider. */
    cacheReadPer1m: numeric('cache_read_per_1m', { precision: 12, scale: 6 }),
    /** Session runtime cost, USD per hour. Nullable if N/A for provider. */
    sessionRuntimePerHour: numeric('session_runtime_per_hour', { precision: 10, scale: 6 }),
    /** Web search cost, USD per request. Nullable if N/A for provider. */
    webSearchPerRequest: numeric('web_search_per_request', { precision: 10, scale: 6 }),
    /** When this rate snapshot became effective. */
    effectiveFrom: timestamp('effective_from', { withTimezone: true }).notNull().defaultNow(),
    /**
     * When this rate snapshot was superseded. NULL = still active.
     * Set when a new version is published; the old row's
     * `effective_until` is backfilled to the new version's
     * `effective_from`.
     */
    effectiveUntil: timestamp('effective_until', { withTimezone: true }),
    /** Who/what created this rate entry (agent id, admin email, 'system'). */
    createdBy: text('created_by').notNull().default('system'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_token_rates_pkey' }),
    // Each (version, model) pair is unique — one rate per model per snapshot.
    uniqueIndex('ops_token_rates_version_model_key').on(t.pricingVersion, t.model),
    // Fast lookup: "what rate does version X use for model Y?" (the derive-cost
    // hot path — one index seek per event ingest).
    index('ops_token_rates_version_idx').on(t.pricingVersion),
    // "Which versions are still active?" (lifecycle management).
    index('ops_token_rates_active_idx')
        .on(t.effectiveFrom)
        .where(sql `${t.effectiveUntil} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_agent_challenge_results — Pull-verification audit log (HB-06, NocoDB #4407)
// ---------------------------------------------------------------------------
/**
 * Records one row per challenge round issued by the challenge-issuer cron.
 *
 * Lifecycle: cron generates a nonce, inserts a row with verdict='timeout'
 * (the worst-case default), then POSTs to ops_agents.callback_url. If the
 * agent responds via POST /api/agents/:agentId/challenge-response within
 * the freshness window, the row is updated to verdict='passed' or 'failed'
 * with responded_at set. If no response arrives, the row stays as 'timeout'.
 * If callback_url was NULL when the cron fired, verdict is 'no_callback'
 * (no outbound HTTP attempted).
 *
 * Three consecutive non-pass verdicts → ops_agents.challenge_failing=true.
 *
 * Why a separate table from ops_agent_activity: pull verification is
 * server-initiated integrity evidence, not agent-emitted work evidence.
 * Mixing them would muddy the activity stream the silent-run watchdog
 * relies on.
 */
export const opsAgentChallengeResults = pgTable('ops_agent_challenge_results', {
    id: uuid('id').notNull().defaultRandom(),
    agentId: text('agent_id').notNull(),
    challengedAt: timestamp('challenged_at', { withTimezone: true }).notNull().defaultNow(),
    nonce: text('nonce').notNull(),
    respondedAt: timestamp('responded_at', { withTimezone: true }),
    verdict: text('verdict').notNull(),
    /** Optional human-readable reason for failed/timeout verdicts. */
    reason: text('reason'),
}, (t) => [
    primaryKey({
        columns: [t.id],
        name: 'ops_agent_challenge_results_pkey',
    }),
    check('ops_agent_challenge_results_verdict_check', sql `${t.verdict} IN ('passed', 'failed', 'timeout', 'no_callback')`),
    index('ops_agent_challenge_agent_time_idx').on(t.agentId, t.challengedAt.desc()),
    // Unique index on (agent_id, nonce) keeps replay attempts on a known
    // nonce from creating duplicate rows or being re-claimed.
    uniqueIndex('ops_agent_challenge_results_nonce_idx').on(t.agentId, t.nonce),
]);
// ---------------------------------------------------------------------------
// ops_vcs_repositories — Repository-to-Organization Mapping (CEO-V3 Phase 2)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// ops_github_installations — GitHub OAuth + Org linking (Sprint 44 E1-S1-T1)
// ---------------------------------------------------------------------------
export const opsGithubInstallations = pgTable('ops_github_installations', {
    id: uuid('id').primaryKey().defaultRandom(),
    // Build.ceo organization this installation belongs to
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    // GitHub App installation ID (from GitHub API)
    installationId: integer('installation_id').notNull().unique(),
    // Installation state
    targetType: text('target_type').notNull(), // 'User' or 'Organization'
    targetId: integer('target_id').notNull(), // GitHub user/org ID
    targetLogin: text('target_login').notNull(), // GitHub username/org name
    // Permissions granted
    permissions: jsonb('permissions').notNull().default({}),
    // Access token (stored encrypted in production)
    accessToken: text('access_token').notNull(),
    // Token expiry — GitHub App installation tokens expire after 1 hour.
    // Used for lazy refresh: if expires_at < now + 5min, refresh token.
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
});
//# sourceMappingURL=schema.js.map